<template>
  <div class="wrapper">
    <SiteHeader
      :user="user"
      :show-chrome="!isTelegramPrivatePage"
      :nav-items="navItems"
      :active-section="activeSection"
      :is-header-scrolled="isHeaderScrolled"
      :is-menu-open="isMenuOpen"
      :upload-disabled="isUploadActionsDisabled"
      :promo-actions-disabled="isPromoActionsDisabled"
      :show-guest-upload-in-mobile="false"
      :show-guest-upload-on-desktop="false"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @nav-click="handleHeaderNavClick"
      @logo-click="redirectToHomePage('link')"
      @open-login="openCommonLoginPopup"
      @guest-upload="openCommonLoginPopup"
      @auth-upload="handleUploadCheckClick"
      @account="redirectToPrivatePage"
      @logout="logout"
    />

    <main class="main main-account">
      <section class="account-info">
        <div class="container container--small">
          <!-- Якорные ссылки навигации сверху -->
          <div class="account-layout">
            <!-- Блок персональных данных: на десктопе липкий сайдбар -->
            <aside
              v-if="!isTelegramPrizesPage"
              id="account-info"
              class="account-content account-sidebar"
            >
              <div class="account-sidebar-card" data-animate="fade-up">
                <button
                  type="button"
                  class="account-sidebar-card__edit"
                  aria-label="Изменить данные профиля"
                  @click="openAccountDetailsPopup"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 4.00001H4C3.46957 4.00001 2.96086 4.21073 2.58579 4.5858C2.21071 4.96087 2 5.46958 2 6.00001V20C2 20.5304 2.21071 21.0392 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0392 20 20.5304 20 20V13M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#01146C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <div class="account-sidebar-card__name">
                  {{ state.user.first_name }} {{ state.user.last_name }}
                </div>
                <div class="account-sidebar-card__contact">{{ formatPhone(state.user.phone_number) }}</div>
                <div class="account-sidebar-card__contact">{{ state.user.email }}</div>
                <button type="button" class="account-sidebar-card__link" @click="openAccountChangePasswordPopup">
                  Изменить пароль
                </button>
                <div v-if="hasUserAddress" class="account-sidebar-card__address">
                  {{ formattedUserAddress }}
                </div>
                <div class="account-sidebar-card__stats">
                  <div class="account-sidebar-card__stat">
                    <span>Принято чеков</span>
                    <strong>{{ state.user.approved_checks_count ?? 0 }}</strong>
                  </div>
                  <div class="account-sidebar-card__stat">
                    <span>Загружено чеков</span>
                    <strong>{{ state.user.checks_count ?? 0 }}</strong>
                  </div>
                </div>
                <button type="button" class="button button-blue account-sidebar-card__upload" :disabled="isUploadActionsDisabled" @click="handleUploadCheckClick">
                  Загрузить чек
                </button>
              </div>
            </aside>

            <div class="account-main">
              <!-- Загруженные чеки -->
              <div
                v-if="!isTelegramPrizesPage"
                id="account-checks"
                class="account-content account-panel account-panel--checks"
              >
                <div class="account-receipts-section" data-animate="fade-up">
                  <h2 class="account-section-title account-section-title--stack">
                    <span>Загруженные чеки</span>
                  </h2>
                  <div v-if="state.checks.results.length === 0" class="account-empty account-empty--checks">
                    <p>Вы еще не загрузили ни один чек</p>
                    <button type="button" class="button button-main" :disabled="isUploadActionsDisabled" @click="handleUploadCheckClick">Загрузить чек</button>
                  </div>
                  <template v-else>
                    <div
                      ref="accountReceiptsList"
                      class="account-receipts-list"
                      :class="{ 'account-receipts-list--few': displayedChecks.length < 5 }"
                    >
                      <div
                        v-for="(receipt, index) in displayedChecks"
                        :key="receipt.id || receipt.__tempId || index"
                        class="account-receipt-card"
                        data-animate="fade-up"
                        :data-animate-delay="String(Math.min(index * 50, 500))"
                      >
                        <div class="account-receipt-card__header">
                          <div
                            class="account-receipt-card__thumb-wrap"
                            @click.stop="getReceiptImages(receipt).length ? openReceiptGallery(receipt, $event) : null"
                          >
                            <div
                              v-if="getReceiptImages(receipt).length"
                              class="account-receipt-card__thumb"
                              :style="{ backgroundImage: `url(${getImageSrc(getReceiptImages(receipt)[0])})` }"
                            >
                              <span class="account-receipt-card__thumb-eye" aria-hidden="true" />
                            </div>
                            <div v-else class="account-receipt-card__thumb account-receipt-card__thumb--empty" />
                          </div>
                          <div
                            class="account-receipt-card__number"
                            @click.stop="getReceiptImages(receipt).length ? openReceiptGallery(receipt, $event) : null"
                          >
                            {{ receipt.number }}
                          </div>
                          <div
                              class="account-receipt-card__status"
                              :class="[getReceiptStatusClass(receipt.status), { 'status-uploading': receipt.__isUploadingPlaceholder }]"
                          >
                            <span v-if="receipt.__isUploadingPlaceholder" class="account-receipt-card__status-loader" aria-hidden="true" />
                            <span class="account-receipt-card__status-text">{{ getReceiptStatusText(receipt.status) }}</span>
                            <button
                                v-if="receipt.status === 'rejected'"
                                type="button"
                                class="account-receipt-card__reject-info"
                                aria-label="Причина отклонения"
                                @mouseenter="onRejectInfoEnter(receipt, $event)"
                                @mouseleave="onRejectInfoLeave"
                                @click.stop="onRejectInfoClick(receipt, $event)"
                            >
                              <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="0.185185" y="0.185185" width="29.6296" height="29.6296" rx="14.8148" fill="#E7ECFD" />
                                <rect x="0.185185" y="0.185185" width="29.6296" height="29.6296" rx="14.8148" stroke="#01146C" stroke-width="0.37037" />
                                <path d="M13.7031 20.7408V14.8152C13.7031 14.2016 14.1973 13.7041 14.8068 13.7041C15.4163 13.7041 15.9105 14.2016 15.9105 14.8152V20.7408C15.9105 21.3545 15.4163 21.852 14.8068 21.852C14.1973 21.852 13.7031 21.3545 13.7031 20.7408ZM14.8217 7.77789C15.4311 7.77798 15.9253 8.27541 15.9253 8.88902C15.9253 9.50263 15.4311 10.0001 14.8217 10.0001H14.8068C14.1973 10.0001 13.7031 9.50268 13.7031 8.88902C13.7031 8.27536 14.1973 7.77789 14.8068 7.77789H14.8217Z" fill="#001B78" fill-opacity="0.4" />
                              </svg>
                            </button>
                          </div>
                          <div class="account-receipt-card__date">{{ formatReceiptDate(receipt.upload_date) }}</div>
                        </div>
                      </div>
                      <div
                        v-if="displayedChecks.length > 0"
                        ref="checksScrollSentinel"
                        class="checks-scroll-sentinel"
                        style="height: 1px; pointer-events: none;"
                      />
                    </div>
                    <div class="account-panel__footer">
                      <button type="button" class="button button-main" :disabled="isUploadActionsDisabled" @click="handleUploadCheckClick">Загрузить чек</button>
                    </div>
                  </template>
                </div>
              </div>

              <!-- Интерактив: казаны -->
              <AccountInteractiveRicePanel
                v-if="!isTelegramPrizesPage"
                root-element-id="account-interactive"
                :won-rice-games="wonRiceGames"
                :chain-access="wonRiceChainAccess"
                @upload-check="handleUploadCheckClick"
              />

              <!-- Мои призы -->
              <div
                v-if="!isTelegramProfilePage"
                id="account-prizes"
                class="account-content account-panel account-panel--prizes"
              >
                <div class="account-prizes-section" data-animate="fade-up">
                  <h2 class="account-section-title">Мои призы</h2>
                  <div v-if="allPrizes.length === 0" class="account-empty">
                    <p>Призов пока нет, загружайте чеки и&nbsp;выигрывайте!</p>
                    <button type="button" class="button button-main" :disabled="isUploadActionsDisabled" @click="handleUploadCheckClick">Загрузить чек</button>
                  </div>
                  <div v-else>
                    <div class="account-prizes-block__desktop">
                      <div class="account-prizes-grid">
                        <div
                          v-for="(prize, index) in allPrizes"
                          :key="prize.id || index"
                          class="account-prize-api-card"
                          data-animate="fade-up"
                          :data-animate-delay="String(Math.min(index * 100, 500))"
                        >
                          <span
                            v-if="getPrizeCounter(prize) > 1"
                            class="account-prize-api-card__counter"
                          >
                            x{{ getPrizeCounter(prize) }}
                          </span>
                          <AccountPrizeInfoBlock
                            v-if="hasPrizeInfo(prize)"
                            :detail="prizeInfoDetail(prize)"
                            :hover-mode="accountPrizesInfoHoverMode"
                            :copied-code="copiedPrizeCode"
                            :format-rich-text="formatPopupRichText"
                            @copy="copyPrizeCode"
                            @download-cert="downloadCertificate"
                          />
                          <div v-if="getPrizeImage(prize)" class="account-prize-api-card__image">
                            <img :src="getPrizeImage(prize)" :alt="getPrizeTitle(prize)">
                          </div>
                          <div class="account-prize-api-card__title">{{ getPrizeTitle(prize) }}</div>
                          <a
                            v-if="getPrizeCardLink(prize)"
                            :href="getPrizeCardLink(prize)"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="account-prize-api-card__link"
                          >
                            Добавить
                          </a>
                          <div
                            v-else-if="getPrizeCardNotice(prize)"
                            class="account-prize-api-card__notice"
                          >
                            {{ getPrizeCardNotice(prize) }}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="account-prizes-block__mobile">
                      <Swiper
                        class="account-prizes-swiper"
                        :modules="accountPrizesSwiperModules"
                        :slides-per-view="1"
                        :space-between="16"
                        :pagination="accountPrizesSwiperPagination"
                        @swiper="onAccountPrizesSwiperInit"
                      >
                        <SwiperSlide
                          v-for="(prize, index) in allPrizes"
                          :key="'p-m-' + (prize.id || index)"
                        >
                          <div class="account-prize-api-card account-prize-api-card--slide">
                            <span
                              v-if="getPrizeCounter(prize) > 1"
                              class="account-prize-api-card__counter"
                            >
                              x{{ getPrizeCounter(prize) }}
                            </span>
                            <AccountPrizeInfoBlock
                              v-if="hasPrizeInfo(prize)"
                              :detail="prizeInfoDetail(prize)"
                              :hover-mode="accountPrizesInfoHoverMode"
                              :copied-code="copiedPrizeCode"
                              :format-rich-text="formatPopupRichText"
                              @copy="copyPrizeCode"
                              @download-cert="downloadCertificate"
                            />
                            <div v-if="getPrizeImage(prize)" class="account-prize-api-card__image">
                              <img :src="getPrizeImage(prize)" :alt="getPrizeTitle(prize)">
                            </div>
                            <div class="account-prize-api-card__title">{{ getPrizeTitle(prize) }}</div>
                            <a
                              v-if="getPrizeCardLink(prize)"
                              :href="getPrizeCardLink(prize)"
                              target="_blank"
                              rel="noopener noreferrer"
                              class="account-prize-api-card__link"
                            >
                              Добавить
                            </a>
                            <div
                              v-else-if="getPrizeCardNotice(prize)"
                              class="account-prize-api-card__notice"
                            >
                              {{ getPrizeCardNotice(prize) }}
                            </div>
                          </div>
                        </SwiperSlide>
                      </Swiper>
                      <div
                        id="account-prizes-swiper-pagination"
                        class="account-prizes-swiper-pagination"
                        aria-hidden="true"
                      />
                    </div>
                  </div>

                  <div
                    v-if="showAccountPrizesLayoutPreview"
                    class="account-prizes-preview-wrap"
                    aria-label="Тестовый превью вёрстки призов"
                  >
                    <p class="account-prizes-preview-label">
                      Тестовый превью (только dev): разные состояния карточки — после правок стилей отключите в&nbsp;коде константу <code>ACCOUNT_PRIZES_LAYOUT_PREVIEW</code>.
                    </p>
                    <div class="account-prizes-grid">
                      <div
                        v-for="(prize, index) in accountPrizesLayoutPreviewList"
                        :key="prize.id || index"
                        class="account-prize-api-card"
                      >
                        <span
                          v-if="getPrizeCounter(prize) > 1"
                          class="account-prize-api-card__counter"
                        >
                          x{{ getPrizeCounter(prize) }}
                        </span>
                        <AccountPrizeInfoBlock
                          v-if="hasPrizeInfo(prize)"
                          :detail="prizeInfoDetail(prize)"
                          :hover-mode="accountPrizesInfoHoverMode"
                          :copied-code="copiedPrizeCode"
                          :format-rich-text="formatPopupRichText"
                          @copy="copyPrizeCode"
                          @download-cert="downloadCertificate"
                        />
                        <div v-if="getPrizeImage(prize)" class="account-prize-api-card__image">
                          <img :src="getPrizeImage(prize)" :alt="getPrizeTitle(prize)">
                        </div>
                        <div class="account-prize-api-card__title">{{ getPrizeTitle(prize) }}</div>
                        <a
                          v-if="getPrizeCardLink(prize)"
                          :href="getPrizeCardLink(prize)"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="account-prize-api-card__link"
                        >
                          Добавить
                        </a>
                        <div
                          v-else-if="prize.__previewNotice"
                          class="account-prize-api-card__notice"
                        >
                          {{ prize.__previewNotice }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>

  <!-- Popup детального просмотра чека -->
  <div v-if="showReceiptPreviewPopup" class="popup popup--receipt-preview" v-popup-tall>
    <div class="popup__overlay" @click="closeReceiptPreviewPopup"></div>
    <div class="popup__window popup__window--large">
      <button @click="closeReceiptPreviewPopup" class="modal-close">
        <svg width='40' height='40'><use href='@/assets/img/icons/icons.svg#x'></use></svg>
      </button>
      <div class="modal-content modal-content--receipt-preview">
        <h5 class="modal-title">{{ receiptPreviewTitle }}</h5>
        <div v-if="receiptPreviewImage" class="receipt-preview__img-wrap">
          <img :src="receiptPreviewImage" alt="" class="receipt-preview__img">
        </div>
      </div>
    </div>
  </div>

  <!-- Popup для изменения данных -->
  <div v-if="showAccountDetailsPopup" class="popup popup--account-details" v-popup-tall>
    <div class="popup__overlay" @click="closeAccountDetailsPopup"></div>
    <div class="popup__window">
      <button @click="closeAccountDetailsPopup" class="modal-close">
        <svg width='40' height='40'><use href='@/assets/img/icons/icons.svg#x'></use></svg>
      </button>
      <div class="modal-content">
        <h5 class="modal-title">Изменение данных</h5>
        <form @submit.prevent="changeAccountDetails" autocomplete="off" class="form">
          <!-- Поле Имя -->
          <div class="field-row" data-cols="1">
            <div class="field-item field-item--floating" data-label="Имя">
              <input type="text" maxlength="255" id="reg-name" v-model="formData.first_name" 
                     @input="(removeSpacesFirstName(), validateAccountDetailsField('first_name'))" 
                     @blur="validateAccountDetailsField('first_name')"
                     name="first_name"
                     :class="{ 'is-error': errors.first_name }" placeholder="">
              <span v-if="errors.first_name" class="error-message">{{ errors.first_name }}</span>
            </div>
          </div>
          <!-- Поле Фамилия -->
          <div class="field-row" data-cols="1">
            <div class="field-item field-item--floating" data-label="Фамилия">
              <input type="text" maxlength="255" id="last-name" v-model="formData.last_name" 
                     @input="(removeSpacesLastName(), validateAccountDetailsField('last_name'))" 
                     @blur="validateAccountDetailsField('last_name')"
                     name="last_name"
                     :class="{ 'is-error': errors.last_name }" placeholder="">
              <span v-if="errors.last_name" class="error-message">{{ errors.last_name }}</span>
            </div>
          </div>
          <!-- Поле Email -->
          <div class="field-row" data-cols="1">
            <div class="field-item field-item--floating" data-label="Номер телефона">
              <input type="text"
                     maxlength="255"
                     id="reg-phone"
                     v-model="formData.phone_number"
                     name="phone_number"
                     :class="{ 'is-error': errors.phone_number }"
                     placeholder=""
                     v-mask
                     @focus="onPhoneFocus"
                     @blur="onPhoneBlur"
                     @input="onPhoneInput">
              <span v-if="errors.phone_number" class="error-message">{{ errors.phone_number }}</span>
            </div>
          </div>
          <div class="field-row field-row-action field-row-action--profile-edit" data-cols="1">
            <div class="field-item">
              <button
                type="submit"
                class="button button-red"
                :class="{ 'button-loader': isAccountDetailsSubmitting }"
                :disabled="!isAccountDetailsFormValid || isAccountDetailsSubmitting"
              >
                <span>Сохранить</span>
              </button>
              <button type="button" class="button button-red button-red--inverse" @click="cancelAccountDetailsEdit">
                Отмена
              </button>
            </div>
          </div>
          <div
              v-if="accountDetailsApiError"
              class="error-message error-message--response"
              role="alert"
          >
            {{ popupErrorMessage(accountDetailsApiError) }}
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="showAccountDetailsSuccess" class="popup popup--account-details-success" v-popup-tall>
    <div class="popup__overlay" @click="closeAccountDetailsSuccess"></div>
    <div class="popup__window">
      <button @click="closeAccountDetailsSuccess" class="modal-close">
        <svg width='40' height='40'><use href='@/assets/img/icons/icons.svg#x'></use></svg>
      </button>
      <div class="modal-content">
        <h5 class="modal-title">Изменение данных</h5>
        <p>
          Ваши данные успешно обновлены
        </p>
      </div>
    </div>
  </div>

  <div v-if="showUserAddressFormPopup" class="popup popup--user-address-form" v-popup-tall>
    <div class="popup__overlay" @click="closeUserAddressFormPopup"></div>
    <div class="popup__window">
      <button @click="closeUserAddressFormPopup" class="modal-close">
        <svg width='40' height='40'><use href='@/assets/img/icons/icons.svg#x'></use></svg>
      </button>
      <div class="modal-content">
        <h5 class="modal-title">Добавить адрес</h5>
        <form @submit.prevent="submitAddressForm" autocomplete="off" class="form">
          <div class="field-row" data-cols="1">
            <div class="field-item field-item--floating" data-label="ФИО">
              <input
                type="text"
                maxlength="255"
                v-model="addressForm.fio"
                :class="{ 'is-error': addressErrors.fio }"
                @input="validateAddressField('fio')"
                @blur="validateAddressField('fio')"
              >
              <span v-if="addressErrors.fio" class="error-message">{{ addressErrors.fio }}</span>
            </div>
          </div>
          <div class="field-row" data-cols="2">
            <div class="field-item field-item--floating" data-label="Номер телефона">
              <input
                type="text"
                maxlength="255"
                v-model="addressForm.phone_number"
                :class="{ 'is-error': addressErrors.phone_number }"
                v-mask
                @input="validateAddressField('phone_number')"
                @blur="validateAddressField('phone_number')"
              >
              <span v-if="addressErrors.phone_number" class="error-message">{{ addressErrors.phone_number }}</span>
            </div>
            <div class="field-item field-item--floating" data-label="Email">
              <input
                type="text"
                maxlength="255"
                v-model="addressForm.email"
                :class="{ 'is-error': addressErrors.email }"
                @input="validateAddressField('email')"
                @blur="validateAddressField('email')"
              >
              <span v-if="addressErrors.email" class="error-message">{{ addressErrors.email }}</span>
            </div>
          </div>
          <div class="field-row" data-cols="2">
            <div class="field-item field-item--floating" data-label="Город">
              <input
                type="text"
                maxlength="255"
                v-model="addressForm.city"
                :class="{ 'is-error': addressErrors.city }"
                @input="validateAddressField('city')"
                @blur="validateAddressField('city')"
              >
              <span v-if="addressErrors.city" class="error-message">{{ addressErrors.city }}</span>
            </div>
            <div class="field-item field-item--floating" data-label="Улица">
              <input
                type="text"
                maxlength="255"
                v-model="addressForm.street"
                :class="{ 'is-error': addressErrors.street }"
                @input="validateAddressField('street')"
                @blur="validateAddressField('street')"
              >
              <span v-if="addressErrors.street" class="error-message">{{ addressErrors.street }}</span>
            </div>
          </div>
          <div class="field-row" data-cols="3">
            <div class="field-item field-item--floating" data-label="Дом">
              <input
                type="text"
                maxlength="255"
                v-model="addressForm.house"
                :class="{ 'is-error': addressErrors.house }"
                @input="validateAddressField('house')"
                @blur="validateAddressField('house')"
              >
              <span v-if="addressErrors.house" class="error-message">{{ addressErrors.house }}</span>
            </div>
            <div class="field-item field-item--floating" data-label="Квартира">
              <input
                type="text"
                maxlength="255"
                v-model="addressForm.apartment"
                :class="{ 'is-error': addressErrors.apartment }"
                @input="validateAddressField('apartment')"
                @blur="validateAddressField('apartment')"
              >
              <span v-if="addressErrors.apartment" class="error-message">{{ addressErrors.apartment }}</span>
            </div>
            <div class="field-item field-item--floating" data-label="Индекс">
              <input
                  type="text"
                  maxlength="6"
                  inputmode="numeric"
                  v-model="addressForm.zip_code"
                  :class="{ 'is-error': addressErrors.zip_code }"
                  @input="sanitizeZipCodeInput"
                  @blur="validateAddressField('zip_code')"
              >
              <span v-if="addressErrors.zip_code" class="error-message">{{ addressErrors.zip_code }}</span>
            </div>
          </div>
          <div class="field-row field-row-action" data-cols="1">
            <div class="field-item">
              <button type="submit" class="button button-red" :disabled="isAddressSubmitting || !isAddressFormValid">
                <span>{{ isAddressSubmitting ? 'ОТПРАВКА...' : 'ОТПРАВИТЬ' }}</span>
              </button>
              <div class="error-message error-message--response" v-if="addressResponseError">{{ popupErrorMessage(addressResponseError) }}</div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>

  <div v-if="showEndPopupSuccess" class="popup popup--end-success" v-popup-tall>
    <div class="popup__overlay" @click="closeShowPromoEnd"></div>
    <div class="popup__window">
      <button @click="closeShowPromoEnd" class="modal-close"></button>
      <div class="popup__content">
        <div class="end-message">
          Акция завершена
        </div>
        <div class="end-message-text">
          Спасибо, что были с нами! Призы вручаются до 22 мая 2026 г.
        </div>
      </div>
    </div>
  </div>

  <!-- Popup для изменения пароля (разметка как «Изменение данных» / формы авторизации) -->
  <div v-if="showAccountChangePasswordPopup" class="popup popup--account-details popup--change-password" v-popup-tall>
    <div class="popup__overlay" @click="closeAccountChangePasswordPopup"></div>
    <div class="popup__window">
      <button type="button" @click="closeAccountChangePasswordPopup" class="modal-close">
        <svg width="40" height="40"><use href="@/assets/img/icons/icons.svg#x"></use></svg>
      </button>
      <div class="modal-content">
        <h5 class="modal-title">Изменение пароля</h5>
        <form @submit.prevent="changeAccountPassword" autocomplete="off" class="form">
          <div class="field-row" data-cols="1">
            <div class="field-item field-item--floating" data-label="Новый пароль">
              <label for="account-password-new">Новый пароль</label>
              <PasswordFieldEyeToggle
                :revealed="passwordVisible"
                @toggle="togglePasswordVisibility"
              />
              <input
                class="form__input"
                :type="passwordVisible ? 'text' : 'password'"
                maxlength="255"
                id="account-password-new"
                v-model="password"
                name="password"
                placeholder="Не менее 6 символов"
                :class="{ 'is-error': errors.password }"
                @keydown.space.prevent
                @input="validatePasswordForm"
                @blur="validatePasswordForm"
              >
              <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
            </div>
          </div>
          <div class="field-row" data-cols="1">
            <div class="field-item field-item--floating" data-label="Повторите пароль">
              <label for="account-password-repeat">Повторите пароль</label>
              <PasswordFieldEyeToggle
                :revealed="passwordCheckVisible"
                @toggle="togglePasswordCheckVisibility"
              />
              <input
                class="form__input"
                :type="passwordCheckVisible ? 'text' : 'password'"
                maxlength="255"
                id="account-password-repeat"
                v-model="password_check"
                name="password_check"
                placeholder="Повторите новый пароль"
                :class="{ 'is-error': errors.password_check }"
                @keydown.space.prevent
                @input="validatePasswordForm"
                @blur="validatePasswordForm"
              >
              <span v-if="errors.password_check" class="error-message">{{ errors.password_check }}</span>
            </div>
          </div>
          <div class="field-row field-row-action field-row-action--profile-edit" data-cols="1">
            <div class="field-item">
              <button
                type="submit"
                class="button button-red"
                :class="{ 'button-loader': isChangePasswordSubmitting }"
                :disabled="!isPasswordFormValid || isChangePasswordSubmitting"
              >
                <span>Сохранить</span>
              </button>
              <button
                type="button"
                class="button button-red button-red--inverse"
                :disabled="isChangePasswordSubmitting"
                @click="closeAccountChangePasswordPopup"
              >
                Отмена
              </button>
              <div class="error-message error-message--response" v-if="responseError">{{ popupErrorMessage(responseError) }}</div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
  <div v-if="showAccountChangePasswordSuccess" class="popup popup--account-details-success popup--change-password-success" v-popup-tall>
    <div class="popup__overlay" @click="closeAccountChangePasswordSuccess"></div>
    <div class="popup__window">
      <button type="button" @click="closeAccountChangePasswordSuccess" class="modal-close">
        <svg width="40" height="40"><use href="@/assets/img/icons/icons.svg#x"></use></svg>
      </button>
      <div class="modal-content">
        <h5 class="modal-title">Изменение пароля</h5>
        <p class="modal-subtitle">Пароль успешно изменён</p>
      </div>
    </div>
  </div>


  <!-- Что-то скопировано -->
  <transition name="fade">
    <div v-if="showCopySuccessPopup" class="mini-message mini-message--copy-success">
    <div class="mini-message-wrap">
        <div class="popup__window">
          <button @click="closeCopySuccessPopup" class="modal-close">
            <svg width='40' height='40'><use href='@/assets/img/icons/icons.svg#x'></use></svg>
          </button>
          <div class="modal-content">
            <div class="modal__points points">
              <div class="points__content">
                Ссылка успешно скопирована.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>

  <!-- Призов не осталось -->
  <div v-if="showEmptyPrizesPopup" class="popup popup--empty-prizes" v-popup-tall>
    <div class="popup__overlay" @click="closeEmptyPrizesPopup"></div>
    <div class="popup__window">
      <button class="modal-close" @click="closeEmptyPrizesPopup">
        <svg width='40' height='40'><use href='@/assets/img/icons/icons.svg?v#x'></use></svg>
      </button>
      <div class="modal-content">
        <h5 class="modal-title">Упс, приз недоступен выбери другой.</h5>
        <p v-if="hasSpecialPriceRemaining" class="modal-subtitle">
          Остаток кофемашин: {{ specialPriceRemainingValue }}
        </p>
        <div class="form form-grid">
          <button class="send-button btn" @click="closeEmptyPrizesPopup">Выбрать</button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showChangePrizesPopup" class="popup popup--change-prizes" v-popup-tall>
    <div class="popup__overlay" @click="closeChangePrizesPopup"></div>
    <div class="popup__window">
      <button class="modal-close" @click="closeChangePrizesPopup">
        <svg width='40' height='40'><use href='@/assets/img/icons/icons.svg?v#x'></use></svg>
      </button>
      <div class="modal-content">
        <h5 class="modal-title">Упс, приз недоступен выбери другой.</h5>
        <p v-if="hasSpecialPriceRemaining" class="modal-subtitle">
          Остаток кофемашин: {{ specialPriceRemainingValue }}
        </p>
        <div class="form form-grid">
          <button class="send-button btn" @click="closeChangePrizesPopup">Выбрать</button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
/* global qrWidget */
/* global _txq */
import {
  getMainSettings,
  getToken,
  getUserInfo,
  changeProfile,
  createUserAddress,
  setAuthHeader,
  getUserChecks,
  changePassword,
  formatFeedbackSendError,
  prefixPopupServerErrorMessage,
  removeToken,
  fetchWinningPrizes,
  fetchAvailablePrizes,
  fetchAdditionalPrizeFile,
  fetchWonRiceGames,
  APMCHECK_API_KEY,
  APMCHECK_API_URL,
} from "@/api";
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import riceGameReady1x from '@/assets/img/rice-game/game-ready.png';
import riceGameReady2x from '@/assets/img/rice-game/game-ready@2x.png';
import riceGameDisabled1x from '@/assets/img/rice-game/game-disabled.png';
import adRiver from '@/services/adriver';
import {ref, onMounted, onUnmounted} from "vue";
import { onClickOutside } from '@vueuse/core';
import { state } from "@/store";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import metrikaMixin from "@/mixins/metrikaMixin";
import { formatPromoRangeRu } from '@/utils/formatPromoRange';
import {
  formatRussianMobileE164,
  isValidRussianMobilePhone,
  russianMobilePhoneHint,
} from '@/utils/russianMobilePhone';
import { registerNameSignificantLength } from '@/utils/registerPersonName';
import SiteHeader from '@/components/layout/SiteHeader.vue';
import PasswordFieldEyeToggle from '@/components/PasswordFieldEyeToggle.vue';
import AccountPrizeInfoBlock from '@/components/AccountPrizeInfoBlock.vue';
import AccountInteractiveRicePanel from '@/components/account/AccountInteractiveRicePanel.vue';
import { appRootKey } from '@/injectionKeys';
import { setMobileMenuScrollLock } from '@/utils/documentScrollLock';
import { mergePendingIntoWonRiceList } from '@/utils/riceGamePending';
import { parseWonRiceGamesResponse } from '@/utils/wonRiceNormalize';
import { blockIfPromoStatusPending, markPromoStatusLoaded } from '@/utils/promoStatus';
import { applyGameStatusFromMainSettings } from '@/utils/gameStatus';

/**
 * Тестовый превью сетки «Мои призы» (показывается только при `npm run serve`).
 * Для проверки на production-сборке локально временно поставьте `true`.
 */
const ACCOUNT_PRIZES_LAYOUT_PREVIEW = false;

export default {
  name: 'PrivatePage',
  mixins: [metrikaMixin],
  inject: {
    shell: {
      from: appRootKey,
      default: null,
    },
  },
  components: {
    SiteHeader,
    PasswordFieldEyeToggle,
    AccountPrizeInfoBlock,
    AccountInteractiveRicePanel,
    Swiper,
    SwiperSlide,
  },
  setup() {
    const activeSection = ref('hero');
    const sections = ['how', 'prizes', 'winners', 'faq', 'contact'];

    const observer = new IntersectionObserver(
        (entries) => {
          let visibleSections = entries
              .filter((entry) => entry.isIntersecting)
              .map((entry) => entry.target.id);

          if (visibleSections.length > 0) {
            activeSection.value = visibleSections[0]; // Берем первую видимую секцию
          } else {
            activeSection.value = 'hero'; // Если ничего не видно — «Главная» (первый блок главной)
          }
        },
        { threshold: 0.5 }
    );

    onMounted(() => {
      sections.forEach((id) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });
    });

    onUnmounted(() => {
      observer.disconnect();
    });


    const results = ref([]);

    const formatPoints = (points) => {
      if (points % 10 === 1 && points % 100 !== 11) {
        return `${points} балл`;
      } else if ([2, 3, 4].includes(points % 10) && ![12, 13, 14].includes(points % 100)) {
        return `${points} балла`;
      } else {
        return `${points} баллов`;
      }
    };

    const formatDateBalls = (isoString) => {
      const date = new Date(isoString);
      return date.toLocaleDateString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };
    return { results, formatDateBalls, state, formatPoints,
      activeSection,
      navItems: [
        { id: 'hero', href: '/#hero', name: 'Главная' },
        { id: 'prizes', href: '/#prizes', name: 'Призы' },
        { id: 'interactive', href: '/#interactive', name: 'Интерактив' },
        { id: 'products', href: '/#products', name: 'Продукция' },
        { id: 'stores', href: '/#stores', name: 'Где купить' },
        { id: 'faq', href: '/#faq', name: 'Вопросы-ответы' },
      ],};
  },
  data() {
    return {
      isPhoneFieldActive: false,
      phoneValidationTimeout: null,
      isMenuOpen: false,
      isOpenDropdownMobile: false,
      selectedText: 'Мои баллы',
      items: [
        { text: 'ЗАГРУЖЕННЫЕ ЧЕКИ', action: 'openChecksTab' },
        { text: 'ИСТОРИЯ ОПЕРАЦИЙ', action: 'openHistoryTab' }
      ],
      activeAccountSection: 'account-info', // Для отслеживания активной секции при скролле
      isMobileNavOpen: false, // Для мобильного выпадающего меню навигации
        rejectTooltip: null, // Для tooltip причины отклонения
        windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1920,
        expandedReceipts: {}, // Для хранения состояния развернутых чеков
      formSending: false,
      changeTitle: null,
      changePoints: null,
      changeID: null,
      prizesFull: [],
      prizesGold: [],
      // prizesPlatinum: [],
      userPrizes: [], // Призы пользователя из /api/quiz/user/prizes
      tabs: [
        { label: "Чеки" },
        { label: "История операций" }
      ],
      history: [],
      limit: 10,
      offset: 0,
      nextPage: null,
      pdfUrl: '',
      info_id: null,
      info_title: null,
      info_text: null,
      info_type: null,
      info_number: null,
      availablePrizes: [],
      receiptId: '',
      birthday: '',
      checkInterval: null,
      checksTotal: 0,
      hasSpecificType: false,
      addressForm: {
        fio: '',
        phone_number: '',
        email: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        zip_code: '',
      },
      addressErrors: {
        fio: '',
        phone_number: '',
        email: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        zip_code: '',
      },
      addressResponseError: '',
      isAddressSubmitting: false,

      isAddressFilled: false,
      isHeaderScrolled: false,
      scrollThreshold: 0,
      isMobileNavOpened: false,
      formData: {
        first_name: '',
        last_name: '',
        phone_number: '',
        email: ''
      },
      user: null,
      password: '',
      password_check: '',
      loginFormErrors: {},
      passwordVisible: false,
      passwordCheckVisible: false,
      rootUrl: window.location.origin,
      prizes: [],
      isOpen: [],
      errors: {},
      showEndPopupSuccess: false,
      showAccountChangePasswordPopup: false,
      isChangePasswordSubmitting: false,
      showEmptyPrizesPopup: false,
      showBirthdayPopup: false,
      showInfoPopup: false,
      showChangePopup: false,
      showChangePrizesPopup: false,
      showAvailablePrizesPopup: false,
      copiedPrizeCode: '',
      copiedPrizeCodeTimer: null,
      showBirthdaySuccessPopup: false,
      showUserAddressFormPopup: false,
      showUserAddressFormPopupSuccess: false,
      showAccountDetailsPopup: false,
      isAccountDetailsSubmitting: false,
      showAccountDetailsSuccess: false,
      showAccountChangePasswordSuccess: false,
      showCopySuccessPopup: false,
      showPromoNotStarted: false,
      showPromoEnd: false,
      showReceiptPreviewPopup: false,
      receiptPreviewTitle: '',
      receiptPreviewImage: '',
      mainSettings: {},
      scrolledAccountSection: -1,
      accountUser: {
        first_name: this.user && this.user.first_name ? this.user.first_name : '',
        last_name: this.last_name && this.user.last_name ? this.user.last_name : '',
        email: this.email && this.user.email ? this.user.email : '',
        phone_number: this.phone_number && this.user.phone_number ? this.user.phone_number : ''
      },
      responseError: '',
      isDisabled: true,
      showSaveCancelButtons: false,
      questionForm: {
        name: this.user && this.user.first_name ? this.user.first_name : '',
        email: this.user && this.user.email ? this.user.email : '',
        text: '',
        agree_personal_data: false
      },
      registerPhoneTouched: false,
      accountDetailsForm: {
        first_name: '',
        last_name: '',
        email: '',
      },
      checks: {
        count: 0,
        next: null,
        previous: null,
        results: [],
      },
      checkOffset: 0,
      checksToShow: 4,
      checksScrollObserver: null,
      checksScrollObservedEl: null,
      checksScrollTarget: null,
      checksScrollTargetInitialized: false,
      checkLimit: 20,
      isChecksLoading: false,
      /** Отложенный повтор GET /user после загрузки чека (бэк иногда отстаёт). */
      receiptCounterRefreshTimer: null,
      wonRiceGames: [],
      /** Цепочка казанов: available | all_won | not_available (корень ответа won-rice). */
      wonRiceChainAccess: 'available',
      accountPrizesSwiper: null,
      accountPrizesSwiperModules: [Pagination],
      accountDetailsApiError: '',
      statusTextMap: {
        0: 'На проверке',
        arrived: 'На проверке',
        approved: 'Одобрен',
        rejected: 'Отклонен',
        won: 'Выиграл',
      },
      accountSections: [
        { name: 'Личные данные', component: 'PersonalData', scrolled: false },
        { name: 'Выигрынные призы', component: 'WinningPrizes', scrolled: false},
        { name: 'Загруженные чеки', component: 'QuizSection', scrolled: false },
      ],
    }
  },
  mounted() {
    // adRiver.trackPageView();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setAuthHeader();
    onClickOutside(this.$refs.dropdown, () => {
      this.isOpenDropdownMobile = false;
    });
    this.loadPrizes();
    Fancybox.bind("[data-fancybox]", {
    });
    this.initializeOpenState();
    this.fetchUsersCheck();
    this.loadPrizes(); // Загружаем призы пользователя
    this.initScrollAnimations();
    this.setupSectionObserver();

    this.syncPromoStatusAvailabilityPopup();

    const token = getToken();
    if (token) {
      getUserInfo().then((userInfo) => {
        if (userInfo) {
          this.applyUserInfo(userInfo);
          this.loadWonRiceGames();
        }
      }).catch((error) => {
        console.error('Failed to fetch user info on mount', error);
      });
    }
    window.addEventListener('scroll', this.handleScrollThrottled, { passive: true });
    window.addEventListener('resize', this.checkMobileNav);
    this.checkMobileNav();
    window.addEventListener('promo-apm-upload-modal-closed', this.handleApmUploadModalClosed);

    // Активируем наблюдение за секциями для подсветки навигации
    this.$nextTick(() => {
      this.highlightNavOnScroll();
      this.initFloatingLabels();
    });
  },
  computed: {
    isPromoActionsDisabled() {
      return !state.promoStatusLoaded;
    },
    isUploadActionsDisabled() {
      return !state.promoStatusLoaded || state.isUploadOpened;
    },
    isTelegramPrivatePage() {
      return this.$route?.meta?.isTelegramPage === true;
    },
    telegramBotUrl() {
      return process.env.VUE_APP_TELEGRAM_BOT_URL || 'https://t.me/';
    },
    isTelegramProfilePage() {
      return this.$route?.meta?.telegramPageType === 'profile';
    },
    isTelegramPrizesPage() {
      return this.$route?.meta?.telegramPageType === 'prizes';
    },
    dropdownStyle() {
      return this.isOpenDropdownMobile ? { top: 'calc(100% + 5px)' } : {};
    },
    allPrizes() {
      // API возвращает { results: [ { prize: { id, title, image, ... }, receipt }, ... ] }
      if (this.userPrizes && Array.isArray(this.userPrizes) && this.userPrizes.length > 0) {
        return this.userPrizes.map((item) => {
          const basePrize = (item && item.prize) ? item.prize : item;
          return {
            ...(basePrize || {}),
            __raw: item,
          };
        });
      }
      
      // Fallback: собираем все призы из всех чеков (если API не вернул данные)
      const prizesMap = new Map();
      
      state.checks.results.forEach(receipt => {
        if (receipt.won_prizes && receipt.won_prizes.length > 0) {
          receipt.won_prizes.forEach(prize => {
            // Показываем только выигранные призы
            if (prize.status === 'won' || !prize.status) {
              // Используем id как ключ, если нет id - используем комбинацию title + type
              const key = prize.id || `${prize.title}-${prize.type}`;
              if (!prizesMap.has(key)) {
                prizesMap.set(key, {
                  id: prize.id,
                  title: prize.title,
                  type: prize.type,
                  status: prize.status,
                  image: prize.image || (Array.isArray(prize.images) && prize.images.length ? prize.images[0] : null)
                });
              }
            }
          });
        }
      });
      
      return Array.from(prizesMap.values());
    },
    rulesUrl() {
      return `${process.env.VUE_APP_API_URL}/api/main/rules`;
    },
    policyUrl() {
      return `${process.env.VUE_APP_API_URL}/api/main/policy`;
    },
    agreementUrl() {
      return `${process.env.VUE_APP_API_URL}/api/main/agreement`;
    },
    advertisingUrl() {
      return `${process.env.VUE_APP_API_URL}/api/main/advertising`;
    },
    cookieUrl() {
      return `${process.env.VUE_APP_API_URL}/api/main/cookie`;
    },
    settingsUrl() {
      return `${process.env.VUE_APP_API_URL}/api/main/settings`;
    },
    specialPriceRemainingValue() {
      return this.mainSettings && this.mainSettings.special_price_remaining;
    },
    hasSpecialPriceRemaining() {
      return this.specialPriceRemainingValue !== null
        && this.specialPriceRemainingValue !== undefined
        && this.specialPriceRemainingValue !== '';
    },
    promoRangeText() {
      return formatPromoRangeRu(this.mainSettings?.start_date, this.mainSettings?.end_date);
    },
    promoStartText() {
      const start = this.mainSettings?.start_date;
      if (!start) {
        return state.promoStart || '';
      }

      const startDate = new Date(start);
      if (Number.isNaN(startDate.getTime())) {
        return state.promoStart || '';
      }

      const formatter = new Intl.DateTimeFormat('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      return `${formatter.format(startDate)} года`;
    },
    displayedChecks() {
      const results = state.checks.results || [];
      return results;
    },
    hasUserAddress() {
      const address = this.state.user?.address || {};
      const fields = ['fio', 'phone_number', 'email', 'city', 'street', 'house', 'apartment', 'zip_code'];
      return fields.some((key) => Boolean(address[key])) || Boolean(this.isAddressFilled);
    },
    formattedUserAddress() {
      const address = this.state.user?.address || {};
      const parts = [
        address.city,
        address.street,
        address.house ? `д. ${address.house}` : '',
        address.apartment ? `кв. ${address.apartment}` : '',
        address.zip_code,
      ].filter(Boolean);
      return parts.join(', ');
    },
    isPasswordFormValid() {
      const p = (this.password || '').trim();
      const pc = (this.password_check || '').trim();
      return (
          p.length >= 6 &&
          pc.length >= 6 &&
          p === pc &&
          !this.errors.password &&
          !this.errors.password_check
      );
    },
    isAddressFormValid() {
      const fio = String(this.addressForm?.fio || '').trim();
      const phone = String(this.addressForm?.phone_number || '').trim();
      const email = String(this.addressForm?.email || '').trim();
      const city = String(this.addressForm?.city || '').trim();
      const street = String(this.addressForm?.street || '').trim();
      const house = String(this.addressForm?.house || '').trim();
      const apartment = String(this.addressForm?.apartment || '').trim();
      const zipCode = String(this.addressForm?.zip_code || '').trim();

      const hasAllRequired = fio && phone && email && city && street && house && apartment && zipCode;
      if (!hasAllRequired) {
        return false;
      }

      const isPhoneValid = isValidRussianMobilePhone(phone);
      const isEmailValid = this.isEmailStrictValid(email);
      const isZipValid = /^\d{6}$/.test(zipCode);
      const hasFieldErrors = Object.values(this.addressErrors || {}).some(Boolean);

      return isPhoneValid && isEmailValid && isZipValid && !hasFieldErrors;
    },
    isAccountDetailsFormValid() {
      const first_name = (this.formData.first_name || '').trim();
      const last_name = (this.formData.last_name || '').trim();
      if (!first_name || registerNameSignificantLength(first_name) < 2) {
        return false;
      }
      if (!last_name || registerNameSignificantLength(last_name) < 2) {
        return false;
      }
      if (!formatRussianMobileE164(this.formData.phone_number)) {
        return false;
      }
      return true;
    },
    /** Как на главной (#prizes): подсказка по ховеру на desktop, по клику на mobile. */
    accountPrizesInfoHoverMode() {
      return this.windowWidth > 767;
    },
    /** Мобильный свайпер «Мои призы» (`#account-prizes-swiper-pagination`). */
    accountPrizesSwiperPagination() {
      return {
        el: '#account-prizes-swiper-pagination',
        clickable: true,
      };
    },
    showAccountPrizesLayoutPreview() {
      return ACCOUNT_PRIZES_LAYOUT_PREVIEW;
    },
    accountPrizesLayoutPreviewList() {
      if (!ACCOUNT_PRIZES_LAYOUT_PREVIEW) {
        return [];
      }
      return [
        {
          id: '__lk-prizes-preview-1',
          title: 'Набор стикеров Mistral',
          image: riceGameReady1x,
          format: 'sticker_pack',
          greeting_text: 'Как получить приз — короткая подсказка для кнопки «i».',
        },
        {
          id: '__lk-prizes-preview-2',
          title: 'Кухонная станция — очень длинное название приза в две–три строки для проверки переносов и высоты карточки',
          images: [riceGameReady2x],
          counter: 3,
        },
        {
          id: '__lk-prizes-preview-3',
          title: 'Промокод на доставку',
          image: riceGameDisabled1x,
          greeting_text: 'Промокод и условия указаны внутри.',
        },
        {
          id: '__lk-prizes-preview-4',
          title: 'Приз без изображения (только заголовок и нижняя зона)',
        },
        {
          id: '__lk-prizes-preview-5',
          title: 'Фирменный мерч',
          image: riceGameReady1x,
          __previewNotice: 'Данные появятся после розыгрыша',
        },
      ];
    },
  },
  watch: {
    activeAccountSection(val) {
      if (val === 'account-checks' && (state.checks.results || []).length > 0) {
        this.$nextTick(() => {
          this.$nextTick(() => this.setupChecksScrollObserver());
        });
      }
    },
    'allPrizes.length'() {
      this.$nextTick(() => this.refreshAccountPrizesSwiper());
    },
    /** Смена режима «до 4 чеков без скролла» ↔ скролл: без этого OS не пересоздаётся после отката refreshForNode (см. osScrollbar). */
    'displayedChecks.length'(newLen, oldLen) {
      const o = typeof oldLen === 'number' ? oldLen : newLen;
      if ((o < 5 && newLen >= 5) || (o >= 5 && newLen < 5)) {
        this.$nextTick(() => {
          window.dispatchEvent(new Event('resize'));
        });
      }
    },
  },
  unmounted() {
    this.clearReceiptCounterRefreshTimer();
    window.removeEventListener('scroll', this.handleScrollThrottled);
    document.body.classList.remove('is-upload-widget-opened');
    if (this._handleScrollRafId) {
      cancelAnimationFrame(this._handleScrollRafId);
      this._handleScrollRafId = null;
    }
    this.hideRejectTooltip();
    if (this.checksScrollObserver && this.checksScrollObservedEl) {
      this.checksScrollObserver.unobserve(this.checksScrollObservedEl);
    }
    this.checksScrollObserver = null;
    this.checksScrollObservedEl = null;
    this.accountPrizesSwiper = null;

    if (this.checksScrollTarget) {
      this.checksScrollTarget.removeEventListener('scroll', this.handleChecksInnerScroll);
      this.checksScrollTarget = null;
      this.checksScrollTargetInitialized = false;
    }
  },
  methods: {
    popupErrorMessage(text) {
      return prefixPopupServerErrorMessage(text);
    },
    normalizeBackendText(text) {
      if (text == null) {
        return '';
      }

      let normalizedText = String(text);
      normalizedText = normalizedText.replace(/&(amp;)+nbsp;/gi, '&nbsp;');
      normalizedText = normalizedText.replace(/&#160;|&#xA0;/gi, '\u00A0');
      normalizedText = normalizedText.replace(/&nbsp;/gi, '\u00A0');
      return normalizedText;
    },
    formatPopupRichText(text) {
      const normalized = this.normalizeBackendText(text);
      let formattedText = normalized.replace(/\r?\n/g, '<br>');
      formattedText = formattedText.replace(/\*\*([\s\S]+?)\*\*/g, '<strong>$1</strong>');
      return formattedText;
    },
    isUrlValue(value) {
      const str = String(value || '').trim();
      return /^https?:\/\//i.test(str) || /^tg:\/\//i.test(str);
    },
    parseAdditionalData(value) {
      if (value == null) {
        return null;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
          return null;
        }
        try {
          return JSON.parse(trimmed);
        } catch (error) {
          return trimmed;
        }
      }
      return value;
    },
    getPrizeField(prize, field) {
      if (!prize) {
        return null;
      }
      const source = prize.__raw || prize;
      const nestedPrize = source.prize || {};
      const variants = [prize[field], source[field], nestedPrize[field]];
      for (const variant of variants) {
        if (variant !== undefined) {
          return variant;
        }
      }
      return null;
    },
    getPrizeFormat(prize) {
      const format = this.getPrizeField(prize, 'format');
      return format ? String(format).trim() : '';
    },
    getPrizeType(prize) {
      const type = this.getPrizeField(prize, 'type');
      return type ? String(type).trim() : '';
    },
    getPrizeGreetingText(prize) {
      const greeting = this.getPrizeField(prize, 'greeting_text');
      const text = this.normalizeBackendText(greeting).trim();
      return text && text !== 'string' ? text : '';
    },
    hasPrizeInfo(prize) {
      return Boolean(this.getPrizeGreetingText(prize));
    },
    getPrizeAdditionalType(prize) {
      const additionalData = this.parseAdditionalData(this.getPrizeField(prize, 'additional_data'));
      if (!additionalData || typeof additionalData !== 'object') {
        return '';
      }
      const type = additionalData.type;
      return type ? String(type).trim().toLowerCase() : '';
    },
    getPrizePopupMode(prize) {
      if (this.getPrizeAdditionalType(prize) === 'promo_code') {
        return 'promo_code';
      }
      return this.getPrizeFormat(prize);
    },
    getPrizeInfoDescription(prize) {
      const greeting = this.getPrizeGreetingText(prize);
      if (greeting) {
        return greeting;
      }
      const description = this.normalizeBackendText(this.getPrizeField(prize, 'description')).trim();
      return description && description !== 'string' ? description : '';
    },
    getPrizeTrackNumberStr(prize) {
      const track = this.getPrizeField(prize, 'track_number');
      return track == null ? '' : String(track).trim();
    },
    getPrizePromoCodeStr(prize) {
      const additionalData = this.parseAdditionalData(this.getPrizeField(prize, 'additional_data'));
      if (!additionalData) {
        return '';
      }
      if (typeof additionalData === 'string') {
        return this.isUrlValue(additionalData) ? '' : additionalData;
      }
      return additionalData.number || additionalData.code || additionalData.promo_code || '';
    },
    getPrizeCertificateFileId(prize) {
      const additionalData = this.parseAdditionalData(this.getPrizeField(prize, 'additional_data'));
      if (!additionalData) {
        return null;
      }
      if (typeof additionalData === 'number') {
        return additionalData;
      }
      if (typeof additionalData === 'string') {
        return this.isUrlValue(additionalData) ? null : (Number.isFinite(Number(additionalData)) ? Number(additionalData) : null);
      }
      const id = additionalData.id ?? additionalData.additional_data_id ?? additionalData.file_id;
      return Number.isFinite(Number(id)) ? Number(id) : null;
    },
    getPrizeCertificateLinkStr(prize) {
      const additionalData = this.parseAdditionalData(this.getPrizeField(prize, 'additional_data'));
      if (!additionalData) {
        return '';
      }
      if (typeof additionalData === 'string') {
        return this.isUrlValue(additionalData) ? additionalData : '';
      }
      const link = additionalData.url || additionalData.link || additionalData.telegram_link || '';
      return this.isUrlValue(link) ? link : '';
    },
    prizeInfoDetail(prize) {
      if (!prize) {
        return { hasInfo: false };
      }
      const hasInfo = this.hasPrizeInfo(prize);
      const format = this.getPrizeFormat(prize);
      return {
        hasInfo,
        image: this.getPrizeImage(prize),
        title: this.getPrizeTitle(prize),
        description: this.getPrizeInfoDescription(prize),
        popupMode: this.getPrizePopupMode(prize),
        type: this.getPrizeType(prize),
        trackNumber: this.getPrizeTrackNumberStr(prize),
        promoCode: this.getPrizePromoCodeStr(prize),
        certificateId: this.getPrizeCertificateFileId(prize),
        certificateLink: this.getPrizeCertificateLinkStr(prize),
        cardLink: this.getPrizeCardLink(prize),
        format,
        isStickerPack: this.isStickerPackFormat(format),
      };
    },
    isEmailStrictValid(email) {
      const normalized = String(email || '')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .trim();
      if (!normalized) {
        return false;
      }
      // Строгая проверка формата:
      // - основная часть начинается с буквы/цифры
      // - домен начинается с буквы/цифры
      // - допустимы только стандартные символы email
      const strictRegex = /^[A-Za-z0-9][A-Za-z0-9._%+-]*@[A-Za-z0-9][A-Za-z0-9.-]*\.[A-Za-z]{2,}$/;
      return strictRegex.test(normalized);
    },
    getFirstError(errorsObj, keys = []) {
      if (!errorsObj || typeof errorsObj !== 'object') return '';
      const values = Array.isArray(keys) && keys.length
        ? keys.map((key) => errorsObj[key])
        : Object.values(errorsObj);
      const firstError = values.find((value) => {
        if (typeof value === 'string') return value.trim() !== '';
        return Boolean(value);
      });
      return typeof firstError === 'string' ? firstError : '';
    },
    validatePhoneNumber() {
      const raw = this.formData.phone_number;
      const d = String(raw || '').replace(/\D/g, '');

      if (isValidRussianMobilePhone(raw)) {
        this.errors.phone_number = '';
        return;
      }

      if (this.isPhoneFieldActive) {
        this.errors.phone_number = '';
        return;
      }

      if (d.length === 0) {
        this.errors.phone_number = 'Введите корректный номер телефона';
        return;
      }

      const completeEnough = d.length >= 11 || (d.length === 10 && d[0] === '9');
      this.errors.phone_number = completeEnough
        ? russianMobilePhoneHint()
        : 'Введите корректный номер телефона';
    },

    // Обработчики событий для поля телефона
    onPhoneFocus() {
      this.isPhoneFieldActive = true;
      // Убираем ошибку при фокусе на поле
      this.errors.phone_number = '';
    },

    onPhoneBlur() {
      this.isPhoneFieldActive = false;
      // Проверяем на blur сразу
      this.validatePhoneNumber();
    },

    onPhoneInput() {
      if (this.showAccountDetailsPopup && this.accountDetailsApiError) {
        this.accountDetailsApiError = '';
      }
      // Отменяем предыдущий таймер
      clearTimeout(this.phoneValidationTimeout);

      // Устанавливаем новый таймер для проверки через 500мс
      this.phoneValidationTimeout = setTimeout(() => {
        this.validatePhoneNumber();
      }, 500);
    },
    validateField(field) {
      switch (field) {
        case 'first_name': {
          this.errors.first_name =
            registerNameSignificantLength(this.registerForm.first_name) < 2
              ? 'Введите имя (минимум 2 символа)'
              : '';
          break;
        }
        case 'name': {
          if (!this.questionForm.name || this.questionForm.name.trim().length < 2) {
            this.feedbackErrors.name = 'Введите имя (минимум 2 символа)';
          } else {
            this.feedbackErrors.name = '';
          }
          break;
        }
        case 'text': {
          this.feedbackErrors.text = this.questionForm.text.length < 2 ? 'Поле обязательно для заполнения' : '';
          break;
        }
        case 'last_name': {
          this.errors.last_name =
            registerNameSignificantLength(this.registerForm.last_name) < 2
              ? 'Введите фамилию (минимум 2 символа)'
              : '';
          break;
        }
        case 'email': {
          const email = this.registerForm.email;
          if (!email || email.trim() === '') {
            this.errors.email = 'Поле обязательно для заполнения';
          } else if (!this.isEmailStrictValid(email)) {
            this.errors.email = 'Введите корректный email';
          } else {
            this.errors.email = '';
          }
          break;
        }
        case 'email_login': {
          const email = this.email;
          this.loginFormErrors.email = this.isEmailStrictValid(email) ? '' : 'Поле обязательно для заполнения';
          break;
        }
        case 'email_feedback': {
          const email = this.questionForm.email;
          if (!email || email.trim() === '') {
            this.feedbackErrors.email = 'Поле обязательно для заполнения';
          } else if (!this.isEmailStrictValid(email)) {
            this.feedbackErrors.email = 'Введите корректный email';
          } else {
            this.feedbackErrors.email = '';
          }
          break;
        }
        case 'phone_number': {
          this.registerPhoneTouched = true;
          const raw = this.registerForm.phone_number;
          const d = String(raw || '').replace(/\D/g, '');

          if (d.length === 0) {
            this.errors.phone_number = 'Поле обязательно для заполнения';
            break;
          }
          if (isValidRussianMobilePhone(raw)) {
            this.errors.phone_number = '';
            break;
          }
          this.errors.phone_number = russianMobilePhoneHint();
          break;
        }
        case 'agree_personal_data': {
          this.errors.agree_personal_data = !this.registerForm.agree_personal_data ? 'Необходимо согласиться на обработку персональных данных' : '';
          break;
        }
        case 'agree_rules': {
          this.errors.agree_rules = !this.registerForm.agree_rules ? 'Необходимо согласиться с правилами акции' : '';
          break;
        }
      }
    },
    highlightNavOnScroll() {
      const sections = ['account-info', 'account-checks', 'account-interactive', 'account-prizes'];

      const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach(entry => {
              // Когда секция видима более чем на 30%
              if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
                // Получаем id элемента
                const id = entry.target.id;

                // Находим все ссылки в навигации
                const navLinks = document.querySelectorAll('.account-page-nav a');

                // Убираем класс 'is-active' у всех ссылок
                navLinks.forEach(link => {
                  link.classList.remove('is-active');
                });

                // Добавляем класс 'is-active' к ссылке, соответствующей текущей секции
                const activeLink = document.querySelector(`.account-page-nav a[href="#${id}"]`);
                if (activeLink) {
                  activeLink.classList.add('is-active');
                }
              }
            });
          },
          {
            threshold: [0.3], // Срабатывает, когда 30% секции видимо
            rootMargin: "-10% 0px -70% 0px" // Смещение области наблюдения, чтобы учесть верхнюю часть экрана
          }
      );

      // Начинаем наблюдение за секциями
      sections.forEach(sectionId => {
        const section = document.getElementById(sectionId);
        if (section) {
          observer.observe(section);
        }
      });

      // Сохраняем observer в свойстве, чтобы иметь возможность отключить его позже
      this.sectionObserver = observer;
    },
    cleanupSectionObserver() {
      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
        this.sectionObserver = null;
      }
    },
    initFloatingLabels() {
      this.$nextTick(() => {
        const floatingFields = document.querySelectorAll('.field-item--floating');
        floatingFields.forEach(fieldItem => {
          const input = fieldItem.querySelector('input, textarea');
          if (!input) return;

          // Функция для проверки и обновления состояния
          const updateFloatingState = () => {
            const hasValue = input.value && input.value.trim().length > 0;
            const hasFocus = document.activeElement === input;
            
            if (hasValue || hasFocus) {
              fieldItem.classList.add('field-item--active');
            } else {
              fieldItem.classList.remove('field-item--active');
            }
          };

          // Удаляем старые обработчики, если они есть
          if (input._floatingHandlers) {
            input.removeEventListener('focus', input._floatingHandlers.focus);
            input.removeEventListener('blur', input._floatingHandlers.blur);
            input.removeEventListener('input', input._floatingHandlers.input);
            input.removeEventListener('change', input._floatingHandlers.change);
          }

          // Создаем новые обработчики
          const focusHandler = () => {
            fieldItem.classList.add('field-item--active');
          };
          const blurHandler = () => {
            updateFloatingState();
          };
          const inputHandler = () => {
            updateFloatingState();
          };
          const changeHandler = () => {
            updateFloatingState();
          };

          // Сохраняем ссылки на обработчики
          input._floatingHandlers = {
            focus: focusHandler,
            blur: blurHandler,
            input: inputHandler,
            change: changeHandler
          };

          // Добавляем обработчики
          input.addEventListener('focus', focusHandler);
          input.addEventListener('blur', blurHandler);
          input.addEventListener('input', inputHandler);
          input.addEventListener('change', changeHandler);

          // Проверяем начальное состояние (без учета фокуса для iOS)
          updateFloatingState();
          requestAnimationFrame(updateFloatingState);
          setTimeout(updateFloatingState, 120);
        });
      });
    },
    validateCyrillic(event) {
      let value = event.target.value.replace(/[^а-яёА-ЯЁ\s-]/g, '');
      this.addressForm.fio = value.replace(/^\s+/, ''); // Убираем пробел в начале
    },
    getAddressFallbackFio() {
      return [this.state.user?.first_name, this.state.user?.last_name].filter(Boolean).join(' ').trim();
    },
    fillAddressFormFromProfile() {
      const userAddress = this.state.user?.address || {};
      const fallbackFio = this.getAddressFallbackFio();
      this.addressForm = {
        fio: userAddress.fio || fallbackFio || '',
        phone_number: userAddress.phone_number || this.state.user?.phone_number || '',
        email: userAddress.email || this.state.user?.email || '',
        city: userAddress.city || '',
        street: userAddress.street || '',
        house: userAddress.house || '',
        apartment: userAddress.apartment || '',
        zip_code: userAddress.zip_code || '',
      };
    },
    sanitizeZipCodeInput() {
      this.addressForm.zip_code = String(this.addressForm.zip_code || '').replace(/\D/g, '').slice(0, 6);
      this.validateAddressField('zip_code');
    },
    validateAddressField(field) {
      const value = String(this.addressForm[field] || '').trim();

      switch (field) {
        case 'fio':
          this.addressErrors.fio = value ? '' : 'Поле обязательно для заполнения';
          break;
        case 'phone_number': {
          const v = String(value || '').trim();
          if (!v) {
            this.addressErrors.phone_number = 'Поле обязательно для заполнения';
            break;
          }
          this.addressErrors.phone_number = isValidRussianMobilePhone(v)
            ? ''
            : russianMobilePhoneHint();
          break;
        }
        case 'email':
          if (!value) {
            this.addressErrors.email = 'Поле обязательно для заполнения';
          } else if (!this.isEmailStrictValid(value)) {
            this.addressErrors.email = 'Введите корректный email';
          } else {
            this.addressErrors.email = '';
          }
          break;
        case 'zip_code':
          this.addressErrors.zip_code = /^\d{6}$/.test(value) ? '' : 'Индекс должен содержать 6 цифр';
          break;
        default:
          this.addressErrors[field] = value ? '' : 'Поле обязательно для заполнения';
      }
    },
    validateAddressForm() {
      const fields = ['fio', 'phone_number', 'email', 'city', 'street', 'house', 'apartment', 'zip_code'];
      fields.forEach((field) => this.validateAddressField(field));
      return !Object.values(this.addressErrors).some(Boolean);
    },
    async submitAddressForm() {
      this.addressResponseError = '';

      if (!this.validateAddressForm()) {
        return;
      }

      const payload = {
        fio: String(this.addressForm.fio || '').trim(),
        phone_number: formatRussianMobileE164(this.addressForm.phone_number),
        email: String(this.addressForm.email || '').trim(),
        city: String(this.addressForm.city || '').trim(),
        street: String(this.addressForm.street || '').trim(),
        house: String(this.addressForm.house || '').trim(),
        apartment: String(this.addressForm.apartment || '').trim(),
        zip_code: String(this.addressForm.zip_code || '').trim(),
      };

      this.isAddressSubmitting = true;
      try {
        await createUserAddress(payload);
        if (!this.state.user) {
          this.state.user = {};
        }
        this.state.user.address = { ...payload };
        this.state.user.address_is_filled = true;
        this.isAddressFilled = true;
        this.closeUserAddressFormPopup();
        this.reload();
      } catch (error) {
        if (error.response && error.response.data) {
          const detail = error.response.data.detail;
          this.addressResponseError = Array.isArray(detail) ? detail.join(', ') : (detail || 'Произошла ошибка при добавлении адреса');
        } else {
          this.addressResponseError = 'Произошла ошибка при добавлении адреса';
        }
      } finally {
        this.isAddressSubmitting = false;
      }
    },
    getChanceEnding(count) {
      const lastDigit = count % 10;
      const lastTwoDigits = count % 100;

      if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
        return 'шансов'; // Исключение для чисел 11-14
      }

      switch (lastDigit) {
        case 1:
          return 'шанс';
        case 2:
        case 3:
        case 4:
          return 'шанса';
        default:
          return 'шансов';
      }
    },
    formatPhone(phone) {
      return phone ? phone.replace(/(\+7)(\d{3})(\d{3})(\d{2})(\d{2})/, '$1 ($2) $3-$4-$5') : ''
    },
    handleDynamicButtonClick(event) {
      if (event.target.id === 'apm-close-widget-button') {
        this.$sendMetrik('reachGoal', 'upload_check_closed');
        state.isUploadOpened = false;
        document.body.classList.remove('is-upload-widget-opened');
      }
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
      this.toggleBodyScroll();
    },
    closeMenu() {
      this.isMenuOpen = false;
      this.toggleBodyScroll();
    },
    toggleBodyScroll() {
      setMobileMenuScrollLock(this.isMenuOpen);
    },
    toggleDropdown() {
      this.isOpenDropdownMobile = !this.isOpenDropdownMobile;
    },
    scrollToSection(sectionId) {
      // Убираем # из начала, если есть
      const cleanSectionId = sectionId.replace('#', '');
      
      // Список секций ЛК (внутренние секции)
      const accountSections = ['account-info', 'account-checks', 'account-interactive', 'account-prizes'];
      
      // Если это секция ЛК, обрабатываем как внутреннюю
      if (accountSections.includes(cleanSectionId)) {
        const section = document.getElementById(cleanSectionId);
        if (section) {
          const header = document.querySelector('.header');
          const headerHeight = header ? header.offsetHeight : 0;
          const sectionTitle = section.querySelector('.account-section-title');
          const targetElement = sectionTitle || section;
          const offset = headerHeight + 8;

          const rawTop = targetElement.getBoundingClientRect().top + window.pageYOffset - offset;
          const sectionTop = Math.max(0, rawTop);

          window.scrollTo({
            top: sectionTop,
            behavior: 'smooth'
          });
          
          this.activeAccountSection = cleanSectionId;
          this.isOpenDropdownMobile = false;
        }
        return;
      }
      
      // Если это секция главной страницы и мы не на главной, переходим на главную
      if (this.$route.path !== '/') {
        // Переходим на главную с хешем
        this.$router.push(`/#${cleanSectionId}`).then(() => {
          // HomePage.vue обработает хеш автоматически через watch
        });
        return;
      }
      
      // Если на главной странице, просто скроллим
      const section = document.getElementById(cleanSectionId);
      if (section) {
        const header = document.querySelector('.header');
        const headerHeight = header ? header.offsetHeight : 0;
        const sectionTop = Math.max(
          0,
          section.getBoundingClientRect().top + window.pageYOffset - headerHeight
        );
        
        window.scrollTo({
          top: sectionTop,
          behavior: 'smooth'
        });
        
        this.activeAccountSection = sectionId;
        this.isOpenDropdownMobile = false;
      }
    },
    handleHeaderNavClick(sectionId) {
      this.$sendMetrik('reachGoal', 'click_nav');
      this.scrollToSection(sectionId);
    },
    handleNavClick(event) {
      event.preventDefault();
      const href = event.target.getAttribute('href') || event.target.closest('a')?.getAttribute('href');
      if (href) {
        const sectionId = href.replace('#', '');
        this.scrollToSection(sectionId);
      }
    },
    callAction(action) {
      if (action === 'openChecksTab') {
        this.scrollToSection('account-checks');
      } else if (action === 'openHistoryTab') {
        this.scrollToSection('account-checks');
      }
    },
    selectItem(item) {
      this.selectedText = item.text;
      this.isOpenDropdownMobile = false;
      if (item.action) {
        this.callAction(item.action);
      }
    },
    copyToClipboard(text) {
      // Пытаемся скопировать информацию в буфер обмена
      navigator.clipboard.writeText(text)
          .then(() => {
            alert('Скопировано');
          })
          .catch(err => {
            console.error('Ошибка при копировании: ', err);
          });
    },
    reload() {
      getUserInfo().then(userInfo => {
        if (userInfo) {
          this.accountUser = {
            first_name: userInfo.first_name,
            last_name: userInfo.last_name,
            phone_number: userInfo.phone_number,
            email: userInfo.email,
          };
          state.user = {
            address: {
              apartment: userInfo.address?.apartment ?? "",
              city: userInfo.address?.city ?? "",
              email: userInfo.address?.email ?? "",
              fio: userInfo.address?.fio ?? "",
              house: userInfo.address?.house ?? "",
              phone_number: userInfo.address?.phone_number ?? "",
              street: userInfo.address?.street ?? "",
              zip_code: userInfo.address?.zip_code ?? "",
            },

            id: userInfo.id,
            uuid: userInfo.uuid,
            first_name: userInfo.first_name,
            last_name: userInfo.last_name,
            phone_number: userInfo.phone_number,
            email: userInfo.email,
            gender: userInfo.gender ?? null,
            is_verification: userInfo.is_verification ?? null,
            birthday: userInfo.birthday,
            is_possible_to_upload_receipt: userInfo.is_possible_to_upload_receipt,
            address_is_filled: userInfo.address_is_filled,
            promocode: userInfo.promocode,
            referral_id: userInfo.referral_id,
            referral_link: userInfo.referral_link,
            balance: userInfo.balance,
            privilege_level: userInfo.privilege_level,
            need_points: userInfo.need_points,
            checks_count: userInfo.checks_count,
            approved_checks_count: userInfo.approved_checks_count,
            rejected_checks_count: userInfo.rejected_checks_count,
            all_cash: userInfo.all_cash
          }
          this.formData = { ...this.accountUser };
          this.questionForm.name = userInfo.first_name;
          this.questionForm.email = userInfo.email;
          this.userId = userInfo.id;
          this.userUuid = userInfo.uuid;
          this.user = {
            first_name: userInfo.first_name,
            last_name_initial: (userInfo.last_name || '').charAt(0),
          };
          this.isAddressFilled = Boolean(userInfo.address_is_filled);
          this.initQrWidget();
        }
      }).catch(error => {
        console.error('Failed to fetch user info on mount', error);
      });
      this.fetchPrizes();
      this.loadPrizes();
      this.initFloatingLabels();
    },
    updated() {
      this.$nextTick(() => {
        this.initFloatingLabels();
      });
    },
    formatDateBalance(dateStr) {
      const date = new Date(dateStr);
      return date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    },
    formatReceiptDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return '';
      const timeZone = 'Europe/Moscow';
      const datePart = new Intl.DateTimeFormat('ru-RU', {
        timeZone,
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(date);
      const timePart = new Intl.DateTimeFormat('ru-RU', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(date);
      return `${datePart}\u00A0\u00A0${timePart}`;
    },
    getReceiptStatusText(status) {
      const statusMap = {
        arrived: 'На проверке',
        approved: 'Одобрен',
        rejected: 'Отклонен',
        won: 'Выиграл',
        pending: 'На модерации',
        processing: 'На модерации',
      };
      return statusMap[status] || 'На проверке';
    },
    getReceiptStatusClass(status) {
      const classMap = {
        'arrived': 'status-pending',    // На проверке
        'approved': 'status-accepted',  // Одобрен
        'rejected': 'status-rejected',  // Отклонен
        'won': 'status-won',            // Выиграл
        'pending': 'status-pending',
        'processing': 'status-pending'
      };
      return classMap[status] || 'status-pending';
    },
    toggleReceiptDetails(receiptId) {
      const isOpen = Boolean(this.expandedReceipts[receiptId]);

      // Аккордеон: открытым может быть только один чек.
      // - если клик по открытому — закрываем его
      // - если клик по закрытому — закрываем все остальные и открываем этот
      this.expandedReceipts = isOpen ? {} : { [receiptId]: true };
    },
    async copyPrizeCode(value) {
      const text = String(value || '').trim();
      if (!text) {
        return;
      }

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus();
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        this.copiedPrizeCode = text;
        if (this.copiedPrizeCodeTimer) {
          clearTimeout(this.copiedPrizeCodeTimer);
        }
        this.copiedPrizeCodeTimer = setTimeout(() => {
          this.copiedPrizeCode = '';
          this.copiedPrizeCodeTimer = null;
        }, 2000);
      } catch (error) {
        // ignore clipboard errors
      }
    },
    openReceiptGallery(receipt, event) {
      // Предотвращаем всплытие события, чтобы не раскрывался блок деталей
      if (event) {
        event.stopPropagation();
      }
      
      const images = this.getReceiptImages(receipt);
      if (images.length === 0) return;
      
      const galleryId = `receipt-${receipt.id}`;
      
      // Проверяем, есть ли уже ссылки для этой галереи
      let existingLinks = document.querySelectorAll(`[data-fancybox="${galleryId}"]`);
      
      // Если ссылок нет, создаем их
      if (existingLinks.length === 0) {
        const tempContainer = document.createElement('div');
        tempContainer.style.display = 'none';
        tempContainer.setAttribute('data-receipt-gallery', galleryId);
        document.body.appendChild(tempContainer);
        
        images.forEach((image, index) => {
          const link = document.createElement('a');
          link.href = this.getImageSrc(image);
          link.setAttribute('data-fancybox', galleryId);
          link.setAttribute('data-caption', `Чек ${receipt.number} - изображение ${index + 1}`);
          tempContainer.appendChild(link);
        });
        
        // Привязываем новые ссылки к Fancybox
        Fancybox.bind(`[data-fancybox="${galleryId}"]`, {});
        
        existingLinks = document.querySelectorAll(`[data-fancybox="${galleryId}"]`);
      }
      
      // Находим первую ссылку и кликаем по ней, чтобы открыть галерею
      if (existingLinks.length > 0) {
        const firstLink = existingLinks[0];
        firstLink.click();
      }
    },
    openReceiptPreviewPopup(receipt) {
      const images = this.getReceiptImages(receipt);
      const first = images.length > 0 ? images[0] : null;
      const dateText = receipt && receipt.upload_date ? this.formatReceiptDate(receipt.upload_date) : '';

      this.receiptPreviewTitle = dateText ? `Чек от ${dateText}` : 'Чек';
      this.receiptPreviewImage = first ? this.getImageSrc(first) : '';

      this.addBodyClass();
      this.showReceiptPreviewPopup = true;
    },
    closeReceiptPreviewPopup() {
      this.removeBodyClass();
      this.showReceiptPreviewPopup = false;
      this.receiptPreviewTitle = '';
      this.receiptPreviewImage = '';
    },
    getPrizeTitle(prize) {
      if (!prize) return '';
      const title = this.normalizeBackendText(this.getPrizeField(prize, 'title')).trim();
      return title === 'string' ? '' : title;
    },
    getPrizeCounter(prize) {
      const source = (prize && prize.__raw) ? prize.__raw : prize;
      const counter = Number(source && source.counter);
      return Number.isFinite(counter) && counter > 0 ? counter : 1;
    },
    getPrizeImage(prize) {
      if (!prize) return '';

      const directImage = prize.image;
      if (typeof directImage === 'string' && directImage.trim()) {
        return directImage.trim();
      }

      const source = prize.__raw || prize;
      const nestedPrize = source.prize || {};
      const images = [
        prize.images,
        source.images,
        nestedPrize.images
      ].find((value) => Array.isArray(value) && value.length > 0);

      if (images && typeof images[0] === 'string') {
        return images[0];
      }

      return '';
    },
    getPrizeCardLink(prize) {
      const format = this.getPrizeFormat(prize);
      if (this.isStickerPackFormat(format)) {
        return process.env.VUE_APP_TELEGRAM_STICKER_PACK_URL || '';
      }
      return '';
    },
    isStickerPackFormat(format) {
      const value = String(format || '').trim();
      return value === 'sticker_pack' || value === 'stickerpack' || value === 'sticker-pack';
    },
    getPrizeCardNotice() {
      // В карточках призов не показываем статусные/служебные тексты.
      // Все такие сообщения выводятся только внутри попапа по кнопке "i".
      return '';
    },
    getDailyPrizeForReceipt(receipt) {
      // Проверяем won_prizes в самом чеке
      if (!receipt || !receipt.won_prizes || !Array.isArray(receipt.won_prizes) || receipt.won_prizes.length === 0) {
        return null;
      }
      
      // Ищем приз типа daily
      // Принимаем приз только если:
      // 1. Тип = 'daily'
      // 2. Есть title (название приза)
      // 3. Статус не отрицательный (не 'rejected', не 'not_won')
      const prize = receipt.won_prizes.find(p => {
        if (!p) {
          return false;
        }
        
        // Проверяем тип приза
        if (p.type !== 'daily') {
          return false;
        }
        
        // Если нет title, приз не валиден
        if (!p.title) {
          return false;
        }
        
        // Если статус указан, проверяем что он не отрицательный
        if (p.status) {
          const negativeStatuses = ['rejected', 'not_won'];
          const statusLower = p.status.toLowerCase();
          // Если статус отрицательный - приз не выигран
          if (negativeStatuses.includes(statusLower)) {
            return false;
          }
          // Если статус есть и он не отрицательный - приз выигран
          return true;
        }
        
        // Если статуса нет, но есть title и type = 'daily' - считаем приз выигранным
        return true;
      });
      
      return prize || null;
    },
    getGrandPrizeForReceipt(receipt) {
      // Проверяем won_prizes в самом чеке
      if (!receipt || !receipt.won_prizes || !Array.isArray(receipt.won_prizes) || receipt.won_prizes.length === 0) {
        return null;
      }
      
      // Ищем приз типа grand
      // Принимаем приз только если:
      // 1. Тип = 'grand'
      // 2. Есть title (название приза)
      // 3. Статус не отрицательный (не 'rejected', не 'not_won')
      const prize = receipt.won_prizes.find(p => {
        if (!p) {
          return false;
        }
        
        // Проверяем тип приза
        if (p.type !== 'grand') {
          return false;
        }
        
        // Если нет title, приз не валиден
        if (!p.title) {
          return false;
        }
        
        // Если статус указан, проверяем что он не отрицательный
        if (p.status) {
          const negativeStatuses = ['rejected', 'not_won'];
          const statusLower = p.status.toLowerCase();
          // Если статус отрицательный - приз не выигран
          if (negativeStatuses.includes(statusLower)) {
            return false;
          }
          // Если статус есть и он не отрицательный - приз выигран
          return true;
        }
        
        // Если статуса нет, но есть title и type = 'grand' - считаем приз выигранным
        return true;
      });
      
      return prize || null;
    },
    getDailyPrizeStatusText(receipt) {
      const prize = this.getDailyPrizeForReceipt(receipt);
      if (!prize) return 'Не выиграл';
      
      // Проверяем статус приза
      if (prize.status === 'not_won' || prize.status === 'rejected') {
        return 'Не выиграл';
      }
      
      if (!prize.status || prize.status === 'pending' || prize.status === 'waiting') {
        return 'Ожидает розыгрыша';
      }
      
      if (prize.status === 'won' || prize.status === 'approved') {
        return prize.title || 'Выиграл приз';
      }
      
      return prize.title || 'Не выиграл';
    },
    getGrandPrizeStatusText(receipt) {
      const prize = this.getGrandPrizeForReceipt(receipt);
      if (!prize) return 'Не выиграл';
      
      if (prize.status === 'not_won' || prize.status === 'rejected' || prize.status === 'not_participant') {
        return 'Ожидает розыгрыша';
      }
      
      // Название приза только если юзер выиграл этот приз; иначе — статус
      if (prize.status === 'won' || prize.status === 'approved') {
        return prize.title || 'Выиграл приз';
      }
      
      // Розыгрыш не произведён — показываем "Ожидает розыгрыша"
      if (!prize.status || prize.status === 'pending' || prize.status === 'waiting') {
        return 'Ожидает розыгрыша';
      }
      
      return prize.title || 'Не выиграл';
    },
    showRejectTooltip(receipt, event) {
      this.hideRejectTooltip();
      const text = receipt.reject_reason || 'Чек отклонен';
      const tooltip = document.createElement('div');
      tooltip.className = 'account-receipt-card__reject-tooltip';
      tooltip.dataset.receiptId = String(receipt.id ?? receipt.__tempId ?? '');
      tooltip.textContent = text;
      document.body.appendChild(tooltip);
      
      const el = event && (event.currentTarget || event.target);
      const rect = el.getBoundingClientRect();
      tooltip.style.left = `${rect.left + rect.width / 2 - tooltip.offsetWidth / 2}px`;
      tooltip.style.top = `${rect.top - tooltip.offsetHeight - 10}px`;
      
      this.rejectTooltip = tooltip;
      this._rejectTooltipScrollHandler = () => this.hideRejectTooltip();
      window.addEventListener('scroll', this._rejectTooltipScrollHandler, true);
    },
    hideRejectTooltip() {
      if (this._rejectTooltipScrollHandler) {
        window.removeEventListener('scroll', this._rejectTooltipScrollHandler, true);
        this._rejectTooltipScrollHandler = null;
      }
      if (this.rejectTooltip) {
        this.rejectTooltip.remove();
        this.rejectTooltip = null;
      }
    },
    applyUserInfo(userInfo) {
      if (!userInfo) return;
      this.accountUser = {
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        phone_number: userInfo.phone_number,
        email: userInfo.email,
      };
      state.user = {
        address: {
          apartment: userInfo.address?.apartment ?? '',
          city: userInfo.address?.city ?? '',
          email: userInfo.address?.email ?? '',
          fio: userInfo.address?.fio ?? '',
          house: userInfo.address?.house ?? '',
          phone_number: userInfo.address?.phone_number ?? '',
          street: userInfo.address?.street ?? '',
          zip_code: userInfo.address?.zip_code ?? '',
        },
        id: userInfo.id,
        uuid: userInfo.uuid,
        first_name: userInfo.first_name,
        last_name: userInfo.last_name,
        phone_number: userInfo.phone_number,
        email: userInfo.email,
        gender: userInfo.gender ?? null,
        is_verification: userInfo.is_verification ?? null,
        birthday: userInfo.birthday,
        is_possible_to_upload_receipt: userInfo.is_possible_to_upload_receipt,
        address_is_filled: userInfo.address_is_filled,
        promocode: userInfo.promocode,
        referral_id: userInfo.referral_id,
        referral_link: userInfo.referral_link,
        balance: userInfo.balance,
        privilege_level: userInfo.privilege_level,
        need_points: userInfo.need_points,
        checks_count: userInfo.checks_count,
        approved_checks_count: userInfo.approved_checks_count,
        rejected_checks_count: userInfo.rejected_checks_count,
        all_cash: userInfo.all_cash,
      };
      this.formData = { ...this.accountUser };
      this.questionForm.name = userInfo.first_name;
      this.questionForm.email = userInfo.email;
      this.userId = userInfo.id;
      this.userUuid = userInfo.uuid;
      this.user = {
        first_name: userInfo.first_name,
        last_name_initial: (userInfo.last_name || '').charAt(0),
      };
      this.isAddressFilled = Boolean(userInfo.address_is_filled);
      this.initQrWidget();
    },
    async loadWonRiceGames() {
      if (!getToken()) return;
      try {
        const data = await fetchWonRiceGames();
        const { items, chainAccess } = parseWonRiceGamesResponse(data);
        this.wonRiceChainAccess = chainAccess;
        const base = Array.isArray(items) ? items : [];
        this.wonRiceGames = mergePendingIntoWonRiceList(base);
      } catch (e) {
        this.wonRiceGames = [];
        this.wonRiceChainAccess = 'available';
      }
    },
    onAccountPrizesSwiperInit(swiper) {
      this.accountPrizesSwiper = swiper;
      this.refreshAccountPrizesSwiper();
    },
    refreshAccountPrizesSwiper() {
      const sw = this.accountPrizesSwiper;
      if (!sw || typeof sw.update !== 'function') return;
      sw.update();
      if (sw.pagination && typeof sw.pagination.update === 'function') {
        sw.pagination.update();
      }
    },
    onRejectInfoEnter(receipt, event) {
      if (this.windowWidth >= 768) {
        this.showRejectTooltip(receipt, event);
      }
    },
    onRejectInfoLeave() {
      if (this.windowWidth >= 768) {
        this.hideRejectTooltip();
      }
    },
    onRejectInfoClick(receipt, event) {
      if (this.windowWidth >= 768) return;
      const rid = String(receipt.id ?? receipt.__tempId ?? '');
      if (this.rejectTooltip && this.rejectTooltip.dataset.receiptId === rid) {
        this.hideRejectTooltip();
        return;
      }
      this.showRejectTooltip(receipt, event);
    },
    setupSectionObserver() {
      // Отслеживаем активную секцию при скролле
      const sections = ['account-info', 'account-checks', 'account-interactive', 'account-prizes'];
      const observerOptions = {
        root: null,
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0
      };

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.activeAccountSection = entry.target.id;
          }
        });
      }, observerOptions);

      this.$nextTick(() => {
        sections.forEach(sectionId => {
          const section = document.getElementById(sectionId);
          if (section) {
            observer.observe(section);
          }
        });
      });
    },
    getActiveTabName() {
      const tabNames = {
        'account-info': 'ЛИЧНЫЕ ДАННЫЕ',
        'account-prizes': 'МОИ ПРИЗЫ',
        'account-checks': 'ЗАГРУЖЕННЫЕ ЧЕКИ',
        'account-interactive': 'ИНТЕРАКТИВ',
      };
      return tabNames[this.activeAccountSection] || 'ЛИЧНЫЕ ДАННЫЕ';
    },
    checkMobileNav() {
      this.windowWidth = window.innerWidth;
      if (this.windowWidth > 767) {
        this.isMobileNavOpen = false;
      }
    },
    formatAmount(amount) {
      return amount > 0 ? `+${amount}` : amount;
    },
    async downloadCertificate(id) {
      try {
        const response = await fetchAdditionalPrizeFile(id);
        // Создаём ссылку для скачивания файла
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;

        // Определяем имя файла
        const filename = response.headers['content-disposition']
            ? response.headers['content-disposition'].split('filename=')[1]
            : `certificate-${id}.pdf`;

        link.setAttribute('download', filename.replace(/"/g, '')); // Убираем кавычки
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Failed to download certificate:", error);
      }
    },
    async loadPrizes() {
      try {
        const response = await fetchWinningPrizes();
        const prizes = response.results || [];
        this.prizes = prizes;
        this.userPrizes = prizes; // Сохраняем призы пользователя
      } catch (error) {
        console.error('Failed to load prizes:', error);
      }
    },
    // Загрузка доступных призов
    async loadAvailablePrizes() {
      try {
        const response = await fetchAvailablePrizes();
        this.availablePrizes = response.results; // Сохраняем доступные призы в data
      } catch (error) {
        this.responseError = 'Не удалось загрузить доступные призы';
      }
    },
    startCheckingForNewChecks() {
      // Если уже идет проверка, не запускаем ещё раз
      if (this.checkInterval) return;

      this.checkInterval = setInterval(async () => {
        try {
          const checksData = await getUserChecks(0, this.checkLimit);
          const nextCount = Number(checksData?.count ?? 0);
          const prevCount = Number(state.checksTotal ?? 0);

          if (nextCount !== prevCount) {
            // Обновляем данные, если количество чеков изменилось
            state.checksTotal = nextCount;
            state.checks.results = checksData.results || [];
            this.checkOffset = 0;
            this.removePendingReceiptPlaceholder();

            // Останавливаем таймер
            this.stopCheckingForNewChecks();

            // Очищаем флаг ожидания
            state.checkSoon = false;
            this.clearReceiptCounterRefreshTimer();
            void this.refreshUserReceiptCounters();
          }
        } catch (error) {
          console.error('Error checking for new checks:', error);
        }
      }, 5000); // Проверяем каждые 5 секунд
    },
    stopCheckingForNewChecks() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    },
    clearReceiptCounterRefreshTimer() {
      if (this.receiptCounterRefreshTimer != null) {
        clearTimeout(this.receiptCounterRefreshTimer);
        this.receiptCounterRefreshTimer = null;
      }
    },
    /** После закрытия попапа виджета чека — актуализируем счётчики в сайдбаре и список (GET /user и чеки). */
    async handleApmUploadModalClosed() {
      void this.refreshUserReceiptCounters();
      this.scheduleReceiptCounterRefreshRetry();
      this.checkOffset = 0;
      await this.fetchUsersCheck();
    },
    scheduleReceiptCounterRefreshRetry() {
      this.clearReceiptCounterRefreshTimer();
      this.receiptCounterRefreshTimer = setTimeout(() => {
        this.receiptCounterRefreshTimer = null;
        void this.refreshUserReceiptCounters();
      }, 4000);
    },
    async refreshUserReceiptCounters() {
      if (!getToken()) return;
      try {
        const userInfo = await getUserInfo();
        if (!userInfo) return;
        state.user.checks_count = userInfo.checks_count ?? 0;
        state.user.approved_checks_count = userInfo.approved_checks_count ?? 0;
        state.user.rejected_checks_count = userInfo.rejected_checks_count ?? 0;
      } catch (e) {
        console.error('refreshUserReceiptCounters', e);
      }
    },
    createPendingReceiptPlaceholder() {
      const nowIso = new Date().toISOString();
      return {
        id: `temp-upload-${Date.now()}`,
        __tempId: `temp-upload-${Date.now()}`,
        __isUploadingPlaceholder: true,
        number: 'Загрузка...',
        upload_date: nowIso,
        status: 'arrived',
        reject_reason: '',
        images: [],
        won_prizes: []
      };
    },
    removePendingReceiptPlaceholder() {
      const current = Array.isArray(state.checks.results) ? state.checks.results : [];
      state.checks.results = current.filter((item) => !item?.__isUploadingPlaceholder);
    },
    addPendingReceiptPlaceholder() {
      this.removePendingReceiptPlaceholder();
      const placeholder = this.createPendingReceiptPlaceholder();
      const current = Array.isArray(state.checks.results) ? state.checks.results : [];
      state.checks.results = [placeholder, ...current];
    },
    clearError(field) {
      this.errors[field] = '';
    },
    clearFeedbackError(field) {
      this.feedbackErrors = { ...this.feedbackErrors, [field]: null };
    },

    initQrWidget() {
      if (!document.getElementById('apm-scan-qr')) {
        qrWidget.init('apm-widget', {
          api: APMCHECK_API_URL,
          apiKey: APMCHECK_API_KEY,
          userUuid: this.userUuid,
          callbacks: {
            onReceiptSentSuccess: () => {
              adRiver.trackReceiptUploadSuccess();
              this.$sendMetrik('reachGoal', 'upload_check_succes');
              if (typeof _txq !== 'undefined') {
                _txq.push(['track', 'check_download_success']);
              }
              // this.executeAdriverEvent('check_download_success');
              // this.executeDSPCounterEvent('check_upload_success');
              state.checkSoon = true;
              state.isUploadOpened = false;
              document.body.classList.remove('is-upload-widget-opened');
              this.addPendingReceiptPlaceholder();
              void this.refreshUserReceiptCounters();
              this.scheduleReceiptCounterRefreshRetry();
              this.startCheckingForNewChecks();
            },
            onReceiptSentError: () => {
              state.isUploadOpened = false;
              document.body.classList.remove('is-upload-widget-opened');
            }
          }
        });
      }
    },
    initScrollAnimations() {
      if (document.documentElement.classList.contains('performance-lite')) {
        this.$nextTick(() => {
          const animatedElements = document.querySelectorAll('[data-animate]');
          animatedElements.forEach(el => {
            const animationType = el.getAttribute('data-animate');
            el.classList.add('animate-on-scroll', `animate-${animationType}`, 'is-animated');
          });
        });
        return;
      }
      // Инициализируем анимации при скролле
      this.$nextTick(() => {
        const observerOptions = {
          root: null,
          rootMargin: '0px 0px 200px 0px',
          threshold: 0.01
        };

        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-animated');
              observer.unobserve(entry.target);
            }
          });
        }, observerOptions);

        const animatedElements = document.querySelectorAll('[data-animate]');
        animatedElements.forEach(el => {
          const animationType = el.getAttribute('data-animate');
          const delay = el.getAttribute('data-animate-delay') || '0';
          
          el.classList.add('animate-on-scroll', `animate-${animationType}`);
          
          // Устанавливаем задержку
          if (delay !== '0') {
            const delayNum = parseInt(delay, 10);
            // Если задержка в стандартных значениях (100, 200, 300, 400, 500), используем класс
            if ([100, 200, 300, 400, 500].includes(delayNum)) {
              el.classList.add(`animate-delay-${delay}`);
            } else {
              // Для других значений используем inline style
              el.style.transitionDelay = `${delayNum / 1000}s`;
            }
          }
          
          observer.observe(el);
        });
      });
    },
    redirectToPrivatePage() {
      this.$router.push('/private');
      this.isMobileNavOpened = false;
    },
    handleRulesButtonClick() {
      this.$sendMetrik('reachGoal', 'Rules_Click');
    },
    async logout() {
      state.checkSoon = false;
      state.checksTotal = 0;
      state.checks = {
        count: 0,
        next: null,
        previous: null,
        results: [],
      };
      state.user = {
        address: {
          apartment: null,
          city: null,
          email: null,
          fio: null,
          house: null,
          phone_number: null,
          street: null,
          zip_code: null,
        },

        first_name: null,
        last_name: null,
        phone_number: null,
        email: null,
        gender: null,
        is_verification: null,
        birthday: null,
        id: null,
        uuid: null,
        is_possible_to_upload_receipt: null,
        address_is_filled: null,
        promocode: null,
        referral_id: null,
        referral_link: null,
        balance: null,
        privilege_level: null,
        need_points: null,
        checks_count: 0,
        approved_checks_count: 0,
        rejected_checks_count: 0,
        all_cash: 0
      };
      const widget = document.getElementById('apm-widget');
      if (widget) {
        widget.innerHTML = ''; // Очистка содержимого виджета
      }

      this.toggleMobileNav(); // Логика для переключения навигации
      removeToken(); // Удаление токена (например, из localStorage)
      this.user = null; // Обновление состояния пользователя
      this.questionForm = { // Очистка формы
        name: '',
        email: '',
        text: '',
        agree_personal_data: false
      };

      // Ожидание завершения обновления DOM
      await this.$nextTick();

      // Выполняем перенаправление после обновления DOM
      window.location.href = '/';
    },
    isPastMainSettingsEndDate() {
      const raw = this.mainSettings?.end_date;
      if (!raw) return false;
      const t = new Date(raw).getTime();
      return !Number.isNaN(t) && Date.now() >= t;
    },
    openCommonLoginPopup() {
      if (blockIfPromoStatusPending()) return;
      if (!this.shell) {
        state.showCommonLoginPopup = true;
        this.addBodyClass();
        this.isMobileNavOpened = false;
        return;
      }
      this.shell.responseError = '';
      if (state.promoStatus == 'not-started' || state.promoStatus == 'future') {
        this.addBodyClass();
        this.isMobileNavOpened = false;
        this.shell.showPromoNotStarted = true;
        return;
      }
      state.showCommonLoginPopup = true;
      this.shell.showCommonLoginPopup = true;
      this.addBodyClass();
      this.isMobileNavOpened = false;
      this.$nextTick(() => {
        if (this.shell.initFloatingLabels) {
          this.shell.initFloatingLabels();
        }
      });
    },
    openLoginPopup() {
      if (blockIfPromoStatusPending()) return;
      state.showCommonLoginPopup = false;
      if (state.promoStatus == 'start' || state.promoStatus == 'end' || this.isPastMainSettingsEndDate()) {
        this.addBodyClass();
        this.isMobileNavOpened = false;
        this.responseError = '';
        this.showLoginPopup = true;
      } else if (state.promoStatus == 'not-started' || state.promoStatus == 'future') {
        this.addBodyClass();
        this.isMobileNavOpened = false;
        this.responseError = '';
        this.shell.showPromoNotStarted = true;
      }
    },
    // handleCombinedClick(target) {
      // this.toggleMobileNav();
      // this.SendMetrick(target);
    // },
    toggleMobileNav() {
      this.isMobileNavOpened = !this.isMobileNavOpened;
    },
    redirectToHomePage() {
      this.$router.push('/');
    },
    addBodyClass() {
      document.body.classList.add('is-popup-opened');
    },
    removeBodyClass() {
      document.body.classList.remove('is-popup-opened');
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },
    togglePasswordCheckVisibility() {
      this.passwordCheckVisible = !this.passwordCheckVisible;
    },
    validatePasswordForm() {
      this.password = (this.password || '').replace(/\s/g, '');
      this.password_check = (this.password_check || '').replace(/\s/g, '');
      this.responseError = '';
      if (!this.errors) {
        this.errors = {};
      }
      const p = this.password || '';
      const pc = this.password_check || '';

      if (p.length > 0 && p.length < 6) {
        this.errors.password = 'Пароль должен быть не менее 6 символов';
      } else {
        this.errors.password = '';
      }

      if (pc.length > 0) {
        if (pc.length < 6) {
          this.errors.password_check = 'Пароль должен быть не менее 6 символов';
        } else if (p !== pc) {
          this.errors.password_check = 'Пароли не совпадают';
        } else {
          this.errors.password_check = '';
        }
      } else {
        this.errors.password_check = '';
      }
    },
    loadMoreChecks() {
      if (!this.isChecksLoading && state.checks.next) {
        this.checkOffset += this.checkLimit;
        this.fetchUsersCheck();
      }
    },
    initializeOpenState() {
      this.isOpen = [...this.prizes, ...state.checks.results].map(() => false);
      state.checksTotal = 0;
      state.checks = {
        count: 0,
        next: null,
        previous: null,
        results: [],
      };
    },
    openBlock(index) {
      this.$nextTick(() => {
        this.isOpen[index] = true; // Установка состояния для открытия блока
      });
    },
    closeBlock(index) {
      this.$nextTick(() => {
        this.isOpen[index] = false; // Установка состояния для закрытия блока
      });
    },
    getImageSrc(imageUrl) {
      // You can add any logic here to manipulate or format the image URL if needed
      return imageUrl;
    },
    getReceiptImages(receipt) {
      if (!receipt || !receipt.images) {
        return [];
      }
      
      // Если images - строка, преобразуем в массив
      if (typeof receipt.images === 'string') {
        // Проверяем, является ли это JSON строкой
        try {
          const parsed = JSON.parse(receipt.images);
          return Array.isArray(parsed) ? parsed : [receipt.images];
        } catch (e) {
          // Если не JSON, возвращаем как массив с одним элементом
          return [receipt.images];
        }
      }
      
      // Если images - массив, возвращаем его
      if (Array.isArray(receipt.images)) {
        return receipt.images;
      }
      
      return [];
    },
    getStatusText(status) {
      return this.statusTextMap[status] || 'Unknown';
    },
    getStatusClass(status) {
      return {
        'status-loaded': status === 0,
        'status-pending': status === 1,
        'status-approved': status === 3,
        'status-rejected': status === 4,
        'status-won': status === 5,
      };
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    },
    formatTime(dateString) {
      const date = new Date(dateString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    handleUploadCheckClick() {
      if (blockIfPromoStatusPending()) return;
      this.$sendMetrik('reachGoal', 'Check_Click');
      if (this.isTelegramProfilePage) {
        adRiver.trackRegisterReceiptClick();
        this.$router.push('/telegram_bot_upload_receipt_page');
        return;
      }
      this.triggerQrScan();
    },
    trackTelegramProfileClick() {
      adRiver.trackTelegramBotClick();
      this.$sendMetrik('reachGoal', 'click_tg_profile');
    },
    triggerQrScan() {
      if (blockIfPromoStatusPending()) return;
      adRiver.trackRegisterReceiptClick();
      // this.executeDSPCounterEvent('buttons_click');
      if (typeof _txq !== 'undefined') {
        _txq.push(['track', 'click_upload_check_after_sign_in']);
      }
      if (state.promoStatus == 'start') {
        const qrElement = document.getElementById('apm-scan-qr');
        if (qrElement) {
          state.isUploadOpened = true;
          document.body.classList.add('is-upload-widget-opened');
          qrElement.click();
        }
      } else if (state.promoStatus == 'not-started') {
        this.addBodyClass();
        this.isMobileNavOpened = false;
        this.shell.showPromoNotStarted = true;
      } else if (state.promoStatus == 'end') {
        this.addBodyClass();
        this.isMobileNavOpened = false;
        this.shell.showPromoEnd = true;
      }
    },
    triggerQuestionForm() {
      const qrElement = document.getElementById('triggerQuestionForm');
      if (qrElement) {
        qrElement.click();
      }
    },
    async fetchUsersCheck() {
      if (this.isChecksLoading) {
        return;
      }
      this.isChecksLoading = true;
      try {
        const checksData = await getUserChecks(this.checkOffset, this.checkLimit);

        if (checksData.count) {
          state.checksTotal = checksData.count;
        }

        if (this.checkOffset === 0) {
          state.checks.results = checksData.results || [];
        } else {
          state.checks.results.push(...(checksData.results || []));
        }

        state.checks.next = checksData.next;

        this.$nextTick(() => {
          Fancybox.bind("[data-fancybox]", {});
            this.$nextTick(() => {
              const host = this.$refs.accountReceiptsList;
              if (host && !this.checksScrollTargetInitialized) {
                const instance = host.__osInstance;
                const viewport = instance && instance.elements ? instance.elements().viewport : host.querySelector('[data-overlayscrollbars-viewport]');
                const scrollTarget = viewport || host;

                this.checksScrollTarget = scrollTarget;
                this.checksScrollTargetInitialized = true;
                scrollTarget.addEventListener('scroll', this.handleChecksInnerScroll, { passive: true });
              }

              this.setupChecksScrollObserver();
              this.handleChecksInnerScroll();
            });
        });

      } catch (error) {
        console.error('Error fetching checks:', error);
      } finally {
        this.isChecksLoading = false;
      }
    },
    setupChecksScrollObserver() {
      const el = this.$refs.checksScrollSentinel;
      if (!el) return;
      const rootEl = this.checksScrollTarget || this.$refs.accountReceiptsList || null;

      if (this.checksScrollObserver) {
        if (this.checksScrollObservedEl) {
          this.checksScrollObserver.unobserve(this.checksScrollObservedEl);
        }
        this.checksScrollObservedEl = el;
        this.checksScrollObserver.observe(el);
        return;
      }
      this.checksScrollObservedEl = el;
      this.checksScrollObserver = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) return;
          if (state.checks.next) {
            this.loadMoreChecks();
          }
        }
      }, { root: rootEl, rootMargin: '120px', threshold: 0 });
      this.checksScrollObserver.observe(el);
    },
    handleChecksInnerScroll() {
      const target = this.checksScrollTarget || this.$refs.accountReceiptsList;
      if (!target || this.isChecksLoading) {
        return;
      }
      if (target.scrollHeight <= target.clientHeight + 1) {
        return;
      }

      const remaining = target.scrollHeight - (target.scrollTop + target.clientHeight);
      if (remaining > 80) {
        return;
      }

      if (state.checks.next) {
        this.loadMoreChecks();
      }
    },
    removeSpacesFirstName() {
      const input = event.target.value.replace(/\s+/g, '');
      // Если открыт попап изменения данных - обновляем formData, иначе accountUser
      if (this.showAccountDetailsPopup) {
        this.formData.first_name = input;
      } else {
        this.accountUser.first_name = input;
      }
    },
    removeSpacesLastName() {
      const input = event.target.value.replace(/\s+/g, '');
      // Если открыт попап изменения данных - обновляем formData, иначе accountUser
      if (this.showAccountDetailsPopup) {
        this.formData.last_name = input;
      } else {
        this.accountUser.last_name = input;
      }
    },
    validateAccountDetailsField(field) {
      switch (field) {
        case 'first_name': {
          this.errors.first_name =
            registerNameSignificantLength(this.formData.first_name) < 2
              ? 'Введите имя (минимум 2 символа)'
              : '';
          break;
        }
        case 'last_name': {
          this.errors.last_name =
            registerNameSignificantLength(this.formData.last_name) < 2
              ? 'Введите фамилию (минимум 2 символа)'
              : '';
          break;
        }
      }
    },
    openAccountDetailsPopup() {
      this.addBodyClass();
      this.formData = {
        first_name: this.accountUser ? this.accountUser.first_name : '',
        last_name: this.accountUser ? this.accountUser.last_name : '',
        phone_number: this.accountUser ? this.accountUser.phone_number : ''
      };
      this.showAccountDetailsPopup = true;
      this.isAccountDetailsSubmitting = false;
      this.accountDetailsApiError = '';
      this.responseError = '';
      this.$nextTick(() => {
        this.initFloatingLabels();
      });
    },
    openAccountDetailsPopupSuccess() {
      this.addBodyClass();
      this.showAccountDetailsSuccess = true;
      this.responseError = '';
    },
    openAccountChangePasswordPopup() {
      this.addBodyClass();
      this.password = '';
      this.password_check = '';
      this.passwordVisible = false;
      this.passwordCheckVisible = false;
      this.errors = {};
      this.responseError = '';
      this.isChangePasswordSubmitting = false;
      this.showAccountChangePasswordPopup = true;
      this.$nextTick(() => {
        this.initFloatingLabels();
      });
    },
    openBirthdayPopup() {
      this.addBodyClass();
      this.showBirthdayPopup = true;
      this.responseError = '';
    },
    closeBirthdayPopup() {
      this.removeBodyClass();
      this.showBirthdayPopup = false;
    },
    closeAccountDetailsPopup() {
      this.removeBodyClass();
      this.showAccountDetailsPopup = false;
      this.isAccountDetailsSubmitting = false;
      this.errors = {};
      this.accountDetailsApiError = '';
      this.responseError = '';
    },
    cancelAccountDetailsEdit() {
      this.closeAccountDetailsPopup();
    },
    openBirthdaySuccessPopup() {
      this.addBodyClass();
      this.showBirthdaySuccessPopup = true;
      this.responseError = '';
    },
    closeBirthdaySuccessPopup() {
      this.removeBodyClass();
      this.showBirthdaySuccessPopup = false;
      this.responseError = '';
    },
    closeAccountChangePasswordPopup() {
      this.removeBodyClass();
      this.showAccountChangePasswordPopup = false;
      this.errors = {};
      this.responseError = '';
      this.isChangePasswordSubmitting = false;
      this.password = '';
      this.password_check = '';
    },
    closeEmptyPrizesPopup() {
      this.selectPrize(this.receiptId);
      this.showEmptyPrizesPopup = false;
    },
    closeChangePrizesPopup() {
      this.fetchPrizes();
      this.showChangePopup = false;
      this.showChangePrizesPopup = false;
      this.removeBodyClass();
    },
    openAddressPopup() {
      this.addBodyClass();
      this.fillAddressFormFromProfile();
      this.addressErrors = {
        fio: '',
        phone_number: '',
        email: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        zip_code: '',
      };
      this.addressResponseError = '';
      this.showUserAddressFormPopup = true;
      this.responseError = '';
      this.$nextTick(() => {
        this.initFloatingLabels();
      });
    },
    closeUserAddressFormPopup() {
      this.removeBodyClass();
      this.showUserAddressFormPopup = false;
      this.responseError = '';
      this.addressResponseError = '';
      this.addressErrors = {
        fio: '',
        phone_number: '',
        email: '',
        city: '',
        street: '',
        house: '',
        apartment: '',
        zip_code: '',
      };
    },
    openShowInfoPopup(text,title,type,number,id) {
      this.info_id = id;
      this.info_number = number;
      this.info_type = type;
      this.info_title = title;
      this.info_text = text;
      this.addBodyClass();
      this.showInfoPopup = true;
      this.responseError = '';
    },
    closeShowInfoPopup() {
      this.removeBodyClass();
      this.showInfoPopup = false;
      this.responseError = '';
    },
    closeCopySuccessPopup() {
      this.removeBodyClass();
      this.showCopySuccessPopup = false;
      this.responseError = '';
    },
    closeUserAddressFormPopupSuccess() {
      this.removeBodyClass();
      this.showUserAddressFormPopupSuccess = false;
      this.responseError = '';
    },
    closeEndPopupSuccess() {
      this.removeBodyClass();
      this.showEndPopupSuccess = false;
      this.responseError = '';
    },
    openAccountChangePasswordPopupSuccess() {
      this.addBodyClass();
      this.showAccountChangePasswordSuccess = true;
      this.responseError = '';
    },
    closeAccountDetailsSuccess() {
      this.removeBodyClass();
      this.showAccountDetailsSuccess = false;
    },
    openChangePopup(title,id,points) {
      this.changeTitle = title;
      this.changeID = id;
      this.changePoints = points;
      this.addBodyClass();
      this.showChangePopup = true;
      this.responseError = '';
    },
    closeChangePopup() {
      this.removeBodyClass();
      this.showChangePopup = false;
      this.responseError = '';
    },
    closeAccountChangePasswordSuccess() {
      this.removeBodyClass();
      this.showAccountChangePasswordSuccess = false;
      this.errors = {};
      this.responseError = '';
    },
    closeShowPromoNotStarted() {
      this.removeBodyClass();
      this.shell.showPromoNotStarted = false;
    },
    closeShowPromoEnd() {
      this.removeBodyClass();
      this.shell.showPromoEnd = false;
    },
    syncPromoStatusAvailabilityPopup() {
      this.shell.showPromoNotStarted = false;
      this.shell.showPromoEnd = false;

      if (state.promoStatus === 'not-started') {
        this.addBodyClass();
        this.shell.showPromoNotStarted = true;
      } else if (state.promoStatus === 'end') {
        this.addBodyClass();
        this.shell.showPromoEnd = true;
      }
    },
    async changeAccountPassword() {
      if (this.isChangePasswordSubmitting) return;
      const password = (this.password || '').trim();
      const password_check = (this.password_check || '').trim();

      this.errors = {};
      this.responseError = '';
      if (password.length < 6) {
        this.errors.password = 'Пароль должен быть не менее 6 символов';
      }
      if (password_check.length < 6) {
        this.errors.password_check = 'Подтверждение пароля должно быть не менее 6 символов';
      }
      if (password !== password_check) {
        this.errors.password_check = 'Пароли не совпадают';
      }

      if (Object.keys(this.errors).length > 0) {
        return;
      }

      this.isChangePasswordSubmitting = true;
      try {
        await changePassword(password);
        this.closeAccountChangePasswordPopup();
        this.openAccountChangePasswordPopupSuccess();
      } catch (error) {
        const data = error?.response?.data;
        this.responseError = data ? formatFeedbackSendError(data) : 'Не удалось изменить пароль';
      } finally {
        this.isChangePasswordSubmitting = false;
      }
    },
    async changeAccountDetails() {
      this.accountDetailsApiError = '';
      const first_name = (this.formData.first_name || '').trim();
      const last_name = (this.formData.last_name || '').trim();
      const phone_number = this.formData.phone_number;

      const formattedPhoneNumber = formatRussianMobileE164(phone_number);

      this.errors = {};

      if (!first_name || registerNameSignificantLength(first_name) < 2) {
        this.errors.first_name = 'Введите имя (минимум 2 символа)';
      }

      if (!last_name || registerNameSignificantLength(last_name) < 2) {
        this.errors.last_name = 'Введите фамилию (минимум 2 символа)';
      }

      if (!formattedPhoneNumber) {
        this.errors.phone_number = russianMobilePhoneHint();
      }

      if (this.errors.first_name || this.errors.last_name || this.errors.phone_number) {
        return;
      }

      this.isAccountDetailsSubmitting = true;
      try {
        await changeProfile(first_name, last_name, formattedPhoneNumber);

        const userInfo = await getUserInfo();
        if (userInfo) {
          this.applyUserInfo(userInfo);
        }

        this.closeAccountDetailsPopup();
      } catch (error) {
        const data = error?.response?.data;
        const parsed = data ? formatFeedbackSendError(data) : '';
        const fallback = 'Что-то пошло не так. Попробуйте позднее';
        const feedbackDefault = 'Не удалось отправить обращение';
        const text = parsed && String(parsed).trim() && parsed !== feedbackDefault ? parsed : fallback;
        this.accountDetailsApiError = text;
      } finally {
        this.isAccountDetailsSubmitting = false;
      }
    },
    handleScrollThrottled() {
      if (this._handleScrollRafId) return;
      this._handleScrollRafId = requestAnimationFrame(() => {
        this._handleScrollRafId = null;
        this.handleScroll();
      });
    },
    handleScroll() {
      this.isHeaderScrolled = window.scrollY > this.scrollThreshold;
    },
  },
  beforeUnmount() {
    setMobileMenuScrollLock(false);
    window.removeEventListener('resize', this.checkMobileNav);
    this.hideRejectTooltip();
    if (this.copiedPrizeCodeTimer) {
      clearTimeout(this.copiedPrizeCodeTimer);
      this.copiedPrizeCodeTimer = null;
    }
    // Очищаем таймер при разрушении компонента
    this.stopCheckingForNewChecks();
    this.clearReceiptCounterRefreshTimer();
    window.removeEventListener('promo-apm-upload-modal-closed', this.handleApmUploadModalClosed);
    document.removeEventListener('click', this.handleDynamicButtonClick);
    window.removeEventListener('scroll', this.handleScrollThrottled);
    if (this._handleScrollRafId) {
      cancelAnimationFrame(this._handleScrollRafId);
      this._handleScrollRafId = null;
    }

    // Очищаем observer
    this.cleanupSectionObserver();
  },
  async created() {
    try {
      const [mainSettingsData] = await Promise.all([
        getMainSettings()
      ]);
      this.mainSettings = mainSettingsData;
      applyGameStatusFromMainSettings(mainSettingsData);
      const mappedStatus = mainSettingsData?.status === 'future'
        ? 'not-started'
        : mainSettingsData?.status === 'active'
          ? 'start'
          : mainSettingsData?.status === 'past'
            ? 'end'
            : '';
      if (mappedStatus) {
        state.promoStatus = mappedStatus;
      }
      this.syncPromoStatusAvailabilityPopup();
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      markPromoStatusLoaded();
    }
  },
};
</script>
